import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Users.module.css';
import {
  Search,
  Download,
  UserPlus,
  MoreVertical,
  Eye,
  EyeOff,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Filter,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  TrendingUp,
  Calendar
} from 'lucide-react';
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import * as XLSX from 'xlsx';
import {
  GET_USERS_LIST,
  GET_USERS_LIST_FILTERED,
  UPDATE_USER_STATUS,
  UPDATE_SELLER_TIER,
  GET_USER_COUNTS,
  ADMIN_CREATE_USER,
  REACTIVATE_USER,
  GET_SALES_REPS,
  SOFT_DELETE_USER,
  GET_ALL_AFFILIATES,
  GET_USER_TEAMS_WITH_SOLD_STATUS,
  GET_AVAILABLE_TEAMS_FOR_USER,
  GET_USERS_PREFERENCE_TEAMS,
  ADD_USER_PREFERENCE_TEAM,
  REMOVE_USER_PREFERENCE_TEAM,
  CLEAR_USER_PREFERENCE_TEAMS,
  SET_USER_TEAMS,
  GET_ALL_LEAGUES,
  GET_ACTIVE_SUPER_USER_USER_IDS
} from "./api/UsersQuery";

import { useNavigate, useLocation } from 'react-router-dom';
import ToasterWithMessage from "../../components/shared-components/toaster";
import { globalContext } from '../../Provider/AppProvider';
import { roles } from '../../helper';
import { config } from '../../config/config';
import { GET_BONUS_AMOUNTS } from '../PriceSetUp/api/PriceSetUp';
import { buildUsersListOrderBy, buildUsersListScopeWhere, buildUsersListWhere } from './utils/buildUsersListFilters';
import { BASELINE_MARKETPLACE_PERCENT } from '../../constants/marketplace-fees';

// Types
interface Team {
  team_name: string;
  team_id?: string;
}

interface League {
  l_id: string;
  l_name: string;
}

interface TeamOption {
  value: string;
  label: string;
  leagueId?: string;
  leagueName?: string;
  categoryId?: string;
}

interface SalesRep {
  sr_id: string;
  sr_first_name: string;
  sr_last_name: string;
  sr_email: string;
  sr_is_active?: boolean;
}

interface Affiliate {
  u_id: string | null;
  a_id?: string;
  a_first_name: string;
  a_last_name: string;
  a_email_id: string;
  u_first_name?: string;
  u_last_name?: string;
  u_email_id?: string;
  u_full_name?: string;
  user?: {
    u_id: string;
    u_full_name: string;
    u_first_name: string;
    u_last_name: string;
    u_email_id: string;
  };
}

interface User {
  u_id: string;
  a_id?: string;
  u_full_name: string;
  u_email_id: string;
  u_is_active: boolean;
  u_created_at: string;
  u_first_name: string;
  u_last_name: string;
  u_last_online: string;
  u_phone_number: string;
  u_is_verified: boolean;
  tickets_sold_amount: number;
  u_stripe_account_id: string;
  u_delete_requested: boolean;
  u_pricing_mode?: string;
  u_fee_percentage?: number | null;
  u_seller_tier?: string;
  u_sales_reps_id?: string | null;
  u_country_code?: string;
  u_affiliate_id?: string | null;
  u_teams?: string[];
  user_preference?: Array<{
    lc_id?: string;
    league_category?: {
      lc_id?: string;
      lc_name?: string;
      lc_abbrev?: string;
      l_id?: string;
    };
  }>;
  u_client_type?: string;
  sales_rep?: SalesRep;
  affiliate?: {
    a_id: string;
    a_first_name: string;
    a_last_name: string;
    a_email_id: string;
    user?: {
      u_id: string;
      u_full_name: string;
      u_first_name: string;
      u_last_name: string;
      u_email_id: string;
    };
  };
  tickets_aggregate?: {
    aggregate?: {
      sum?: {
        t_active_tickets?: number;
        t_sold_tickets?: number;
        t_list_price?: number;
      };
    };
  };
  tickets?: Array<{
    ticket_placements_aggregate?: {
      aggregate?: {
        sum?: {
          tp_list_price?: number;
        };
      };
    };
  }>;
}

interface NewUserInput {
  first_name: string;
  last_name: string;
  business_name: string;
  email: string;
  phone: string;
  country_code: string;
  client_type: string;
  seller_tier: string;
  sales_rep_id: string;
  affiliate_id: string;
  selected_teams: string[];
  password: string;
  confirmPassword: string;
  super_user_percentage: string;
}

// Date range filter type
interface DateRange {
  from: Date | null;
  to: Date | null;
}

const DATE_SORT_COLUMNS = new Set(['lastActive', 'memberSince']);

const Users = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { error: showError, success } = ToasterWithMessage();
  const apolloClient = useApolloClient();
  const { globalState } = useContext<any>(globalContext);
  // Match Affiliates / Userdetails: GraphQL uses x-hasura-user-role; profile alone can still say marketing_user.
  const activeHasuraRole = (localStorage.getItem("x-hasura-user-role") || "").toLowerCase().trim();
  const isMarketingUser = false;
  const isAffiliateAdmin = activeHasuraRole === roles.AFFILIATER_ADMIN;
  const canReadSalesReps =
    activeHasuraRole === roles.ADMIN ||
    activeHasuraRole === roles.SUB_ADMIN ||
    activeHasuraRole === roles.MARKETING_USER ||
    activeHasuraRole === roles.AFFILIATER_ADMIN;
  const canManageSuperUserTable =
    activeHasuraRole === roles.ADMIN ||
    activeHasuraRole === roles.SUB_ADMIN ||
    activeHasuraRole === roles.MARKETING_USER;
  const loggedInEmail = (globalState?.profile?.mailId || "").toLowerCase().trim();
  
  // State Management
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageOffset, setPageOffset] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState('active');
  const [clientType, setClientType] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string>("");
  const [selectedSalesRep, setSelectedSalesRep] = useState<string | null>(null);
  const [selectedAffiliate, setSelectedAffiliate] = useState<string | null>(null);
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [superUserToggleUserId, setSuperUserToggleUserId] = useState<string | null>(null);
  /** Optimistic toggle state when promote/deactivate succeeds but super_user refetch is blocked by role */
  const [superUserToggleOverrides, setSuperUserToggleOverrides] = useState<Record<string, boolean>>({});
  const [showClientTypeFilter, setShowClientTypeFilter] = useState(false);
  const [showTeamFilter, setShowTeamFilter] = useState(false);
  const [teamFilterSearch, setTeamFilterSearch] = useState('');

  useEffect(() => {
    if (!showTeamFilter) {
      setTeamFilterSearch('');
    }
  }, [showTeamFilter]);

  const [sortColumn, setSortColumn] = useState<string>('lastActive');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
const [teamLeagueFilter, setTeamLeagueFilter] = useState('all');

  // Date filter states
  const [showDateFilterMenu, setShowDateFilterMenu] = useState(false);
  const [activeDateFilter, setActiveDateFilter] = useState<'lastActive' | 'memberSince' | null>(null);
  const [lastActiveRange, setLastActiveRange] = useState<DateRange>({ from: null, to: null });
  const [memberSinceRange, setMemberSinceRange] = useState<DateRange>({ from: null, to: null });
  
  // Cell dropdown states
  const [showTeamsDropdown, setShowTeamsDropdown] = useState<string | null>(null);
  const [showSalesRepDropdown, setShowSalesRepDropdown] = useState<string | null>(null);
  const [showAffiliateDropdown, setShowAffiliateDropdown] = useState<string | null>(null);
  const [salesRepSearchTerm, setSalesRepSearchTerm] = useState('');
  const [affiliateSearchTerm, setAffiliateSearchTerm] = useState('');
  
  // Filtered users state
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  // Refs/map to anchor dropdowns and positions for portal rendering
  const dropdownAnchorRefs = useRef(new Map<string, HTMLButtonElement | null>());
  const [salesRepMenuPos, setSalesRepMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [affiliateMenuPos, setAffiliateMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [teamsMenuPos, setTeamsMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const ACTION_MENU_WIDTH = 180;
  const ACTION_MENU_HEIGHT = 220;

  const computeActionMenuPos = (button: HTMLButtonElement) => {
    const rect = button.getBoundingClientRect();
    let top = rect.bottom + 4;
    if (top + ACTION_MENU_HEIGHT > window.innerHeight - 8) {
      top = Math.max(8, rect.top - ACTION_MENU_HEIGHT - 4);
    }
    const left = Math.max(
      8,
      Math.min(rect.right - ACTION_MENU_WIDTH, window.innerWidth - ACTION_MENU_WIDTH - 8),
    );
    return { top: Math.round(top), left: Math.round(left), width: ACTION_MENU_WIDTH };
  };

  const closeActionMenu = () => {
    setOpenActionMenu(null);
    setActionMenuPos(null);
  };

  // Recompute open menu positions when scroll/resize happens so menus stay anchored
  useEffect(() => {
    const recompute = () => {
      if (showSalesRepDropdown) {
        const el = dropdownAnchorRefs.current.get(`sales-${showSalesRepDropdown}`);
        if (el) {
          const r = el.getBoundingClientRect();
          setSalesRepMenuPos({ top: Math.round(r.bottom), left: Math.round(r.left), width: Math.round(r.width) });
        }
      }
      if (showAffiliateDropdown) {
        const el = dropdownAnchorRefs.current.get(`aff-${showAffiliateDropdown}`);
        if (el) {
          const r = el.getBoundingClientRect();
          setAffiliateMenuPos({ top: Math.round(r.bottom), left: Math.round(r.left), width: Math.round(r.width) });
        }
      }
      if (showTeamsDropdown) {
        const el = dropdownAnchorRefs.current.get(`teams-${showTeamsDropdown}`);
        if (el) {
          const r = el.getBoundingClientRect();
          setTeamsMenuPos({ top: Math.round(r.bottom), left: Math.round(r.left), width: Math.round(r.width) });
        }
      }
      if (openActionMenu) {
        const el = dropdownAnchorRefs.current.get(`action-${openActionMenu}`);
        if (el) {
          setActionMenuPos(computeActionMenuPos(el));
        }
      }
    };

    const handler = () => recompute();

    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    const tbl = tableContainerRef.current;
    if (tbl) tbl.addEventListener('scroll', handler, { passive: true });

    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
      if (tbl) tbl.removeEventListener('scroll', handler);
    };
  }, [showSalesRepDropdown, showAffiliateDropdown, showTeamsDropdown, openActionMenu]);
  
  // Add User Dialog State
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [pendingDeleteUser, setPendingDeleteUser] = useState<User | null>(null);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newUser, setNewUser] = useState<NewUserInput>({
    first_name: "",
    last_name: "",
    business_name: "",
    email: "",
    phone: "",
    country_code: "+1",
    client_type: "concierge",
    seller_tier: "",
    sales_rep_id: "",
    affiliate_id: "",
    selected_teams: [],
    password: "",
    confirmPassword: "",
    super_user_percentage: "20",
  });

  const [formErrors, setFormErrors] = useState<{
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    country_code?: string;
    password?: string;
    confirmPassword?: string;
    super_user_percentage?: string;
  }>({});
  
  const [userCounts, setUserCounts] = useState({
    all: 0,
    active: 0,
    deactivated: 0,
    deleted: 0
  });

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [shouldLoadBonusAmounts, setShouldLoadBonusAmounts] = useState(false);
  const [shouldLoadAuxiliaryAffiliates, setShouldLoadAuxiliaryAffiliates] = useState(false);
  const [shouldLoadAvailableTeams, setShouldLoadAvailableTeams] = useState(false);

  const shouldFetchAffiliates = isAffiliateAdmin || shouldLoadAuxiliaryAffiliates;

  useEffect(() => {
    if (showAddDialog) {
      setShouldLoadBonusAmounts(true);
      setShouldLoadAuxiliaryAffiliates(true);
    }
  }, [showAddDialog]);

  useEffect(() => {
    if (showAffiliateDropdown) {
      setShouldLoadAuxiliaryAffiliates(true);
    }
  }, [showAffiliateDropdown]);

  useEffect(() => {
    if (showTeamFilter || showTeamsDropdown) {
      setShouldLoadAvailableTeams(true);
    }
  }, [showTeamFilter, showTeamsDropdown]);

  const { data: bonusAmountsData } = useQuery(GET_BONUS_AMOUNTS, {
    skip: !shouldLoadBonusAmounts,
    fetchPolicy: 'cache-first',
  });

  const defaultConciergePercentage = React.useMemo(() => {
    const quickCutAmount = Number(bonusAmountsData?.earning_configuration?.[0]?.referral_amount);
    if (Number.isFinite(quickCutAmount) && quickCutAmount > 0) {
      return String(quickCutAmount + BASELINE_MARKETPLACE_PERCENT);
    }
    return "20";
  }, [bonusAmountsData]);

  useEffect(() => {
    setNewUser((prev) => {
      if (prev.client_type !== "concierge") return prev;
      if (prev.super_user_percentage && prev.super_user_percentage !== "20") return prev;
      if (prev.super_user_percentage === defaultConciergePercentage) return prev;
      return { ...prev, super_user_percentage: defaultConciergePercentage };
    });
  }, [defaultConciergePercentage]);

  const normalizeClientType = (value?: string | null): string => {
    if (!value) return '';
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, '-');
  };

  const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || "").replace(/\/+$/, "");

  const getNodeAuthHeaders = () => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes("idToken")) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : "";
    const hasuraRole = localStorage.getItem("x-hasura-user-role") || "";
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`;
    }
    if (hasuraRole) {
      headers["x-hasura-role"] = hasuraRole;
    }
    return headers;
  };

  const callNodeApi = async (endpoint: string, body: Record<string, unknown>) => {
    if (!nodeApiBaseUrl) {
      throw new Error("REACT_APP_NODE_API_URL is not configured");
    }

    const response = await fetch(`${nodeApiBaseUrl}${endpoint}`, {
      method: "POST",
      headers: getNodeAuthHeaders(),
      body: JSON.stringify(body),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responseData?.message || `Request failed with status ${response.status}`);
    }
    return responseData;
  };

  const assignAffiliateViaApi = async (userId: string, affiliateId: string | null) => {
    return callNodeApi("/api/assignAffiliate", { input: { userId, affiliateId } });
  };

  const assignSalesRepViaApi = async (userId: string, salesRepId: string | null) => {
    return callNodeApi("/api/assignSalesRep", { input: { userId, salesRepId } });
  };

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPageOffset(0);
  }, [
    debouncedSearch,
    activeFilter,
    clientType,
    selectedTeam,
    selectedSalesRep,
    selectedAffiliate,
    lastActiveRange,
    memberSinceRange,
    sortColumn,
    sortDirection,
  ]);

  // Get status filter from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusFilter = params.get('status_filter');
    if (statusFilter && ['active', 'deactivated', 'deleted', 'all'].includes(statusFilter)) {
      setActiveFilter(statusFilter);
    }
  }, [location]);

  // Click outside handler for dropdowns and action menu
  useEffect(() => {
    const handleClickOutside = () => {
      if (openActionMenu) {
        closeActionMenu();
      }
      if (showSalesRepDropdown) {
        setShowSalesRepDropdown(null);
      }
      if (showAffiliateDropdown) {
        setShowAffiliateDropdown(null);
      }
      if (showTeamsDropdown) {
        setShowTeamsDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openActionMenu, showAffiliateDropdown, showSalesRepDropdown, showTeamsDropdown]);

  // Fetch available teams and user preference team sold status for the active dropdown
  const { data: availableTeamsData, loading: availableTeamsLoading, refetch: refetchAvailableTeams } = useQuery<{
    league_categories: Array<{ lc_id: string; lc_abbrev: string; lc_name: string; lc_colour_code?: string; lc_brand_name?: string; l_id?: string }>
  }>(GET_AVAILABLE_TEAMS_FOR_USER, {
    skip: !shouldLoadAvailableTeams,
    fetchPolicy: 'cache-first',
  });
  const { data: teamsWithSoldData, loading: teamsWithSoldLoading, refetch: refetchTeamsWithSoldStatus } = useQuery(GET_USER_TEAMS_WITH_SOLD_STATUS, {
    variables: { userId: showTeamsDropdown },
    skip: !showTeamsDropdown,
    fetchPolicy: 'network-only'
  });
  const teamsWithSoldStatus = React.useMemo(() => {
    const map = new Map<string, boolean>();
    const soldTicketEvents = Array.isArray(teamsWithSoldData?.ticket_placement)
      ? teamsWithSoldData.ticket_placement
          .map((placement: any) => placement?.ticket?.event?.e_name?.toLowerCase() || '')
      : [];

    if (Array.isArray(teamsWithSoldData?.user_preference)) {
      teamsWithSoldData.user_preference.forEach((pref: any) => {
        if (!pref?.lc_id) return;
        const teamName = (pref?.league_category?.lc_name || '').toLowerCase();
        const teamAbbrev = (pref?.league_category?.lc_abbrev || '').toLowerCase();
        const hasSold = soldTicketEvents.some((eventName: string) =>
          (teamName && eventName.includes(teamName)) ||
          (teamAbbrev && eventName.includes(teamAbbrev))
        );
        map.set(pref.lc_id, hasSold);
      });
    }
    return map;
  }, [teamsWithSoldData]);
  const { data: allLeaguesData } = useQuery<{ leagues: League[] }>(GET_ALL_LEAGUES, {
    fetchPolicy: 'cache-first',
  });

  const [optimisticTeamNamesByUserId, setOptimisticTeamNamesByUserId] = useState<Record<string, string[]>>({});

  const teamsLoading = availableTeamsLoading;

  // Fetch sales reps
  const { data: salesRepsData, loading: salesRepsLoading, refetch: refetchSalesReps } = useQuery<{ salesReps: SalesRep[] }>(GET_SALES_REPS, {
    skip: !canReadSalesReps,
    fetchPolicy: 'cache-first',
    onError: (err) => {
      console.error('Error fetching sales reps:', err);
      if (canReadSalesReps) {
        showError('Failed to load sales reps');
      }
    }
  });

  // Fetch all affiliates
  const { data: affiliatesData, loading: affiliatesLoading, refetch: refetchAffiliates } = useQuery(GET_ALL_AFFILIATES, {
    skip: !shouldFetchAffiliates,
    variables: {
      pageSize: 1000,
      pageNumber: 0,
      order_by: {},
      search_event: "%"
    },
    fetchPolicy: 'cache-first',
    onError: (err) => {
      console.error('Error fetching affiliates:', err);
      if (!isMarketingUser) {
        showError('Failed to load affiliates');
      }
    }
  });

  // Map affiliates for dropdown
  const affiliatesList = React.useMemo((): Array<{
    a_id: string;
    u_id: string | null;
    u_full_name: string;
    u_email_id: string;
    u_first_name: string;
    u_last_name: string;
    a_first_name: string;
    a_last_name: string;
    a_email_id: string;
  }> => {
    if (Array.isArray(affiliatesData?.affiliates)) {
      return affiliatesData.affiliates.map((item: any) => ({
        a_id: item.a_id,
        u_id: item.user?.u_id,
        u_full_name:
          item.user?.u_full_name ||
          `${item.a_first_name || ''} ${item.a_last_name || ''}`.trim() ||
          item.a_email_id ||
          (item.a_id ? `Affiliate ${String(item.a_id).slice(0, 8)}` : "Affiliate"),
        u_email_id: item.a_email_id,
        u_first_name: item.a_first_name,
        u_last_name: item.a_last_name,
        a_first_name: item.a_first_name,
        a_last_name: item.a_last_name,
        a_email_id: item.a_email_id
      })).filter((a: any) => a.a_id);
    }
    return [];
  }, [affiliatesData]);

  const filteredAffiliates = React.useMemo(() => {
    const q = affiliateSearchTerm.trim().toLowerCase();
    if (!q) return affiliatesList;
    return affiliatesList.filter((a: any) => {
      const name = (a.u_full_name || '').toLowerCase();
      const email = (a.u_email_id || '').toLowerCase();
      return name.includes(q) || email.includes(q) || (String(a.a_id || '')).toLowerCase().includes(q);
    });
  }, [affiliateSearchTerm, affiliatesList]);

  // Create a map of affiliate ID to affiliate details for quick lookup
  const affiliatesMap = React.useMemo(() => {
    const map = new Map();
    affiliatesList.forEach((affiliate: any) => {
      if (affiliate?.a_id) {
        map.set(affiliate.a_id, affiliate);
      }
      if (affiliate?.u_id) {
        map.set(affiliate.u_id, affiliate);
      }
    });
    return map;
  }, [affiliatesList]);

  const loggedInAffiliate = React.useMemo(() => {
    if (!isAffiliateAdmin || !loggedInEmail) return null;
    return (
      affiliatesList.find(
        (affiliate: any) => (affiliate.u_email_id || "").toLowerCase().trim() === loggedInEmail
      ) || null
    );
  }, [affiliatesList, isAffiliateAdmin, loggedInEmail]);

  const scopedAffiliateId = loggedInAffiliate?.a_id || null;

  useEffect(() => {
    if (!isAffiliateAdmin || !scopedAffiliateId) return;
    setNewUser((prev) =>
      prev.affiliate_id === scopedAffiliateId ? prev : { ...prev, affiliate_id: scopedAffiliateId }
    );
  }, [isAffiliateAdmin, scopedAffiliateId]);

  useEffect(() => {
    if (selectedTeam) {
      setShouldLoadAvailableTeams(true);
    }
  }, [selectedTeam]);

  const applyAggregateUserCounts = (countsData: {
    activeUsers?: { aggregate?: { count?: number } };
    deactivatedUsers?: { aggregate?: { count?: number } };
    deletedUsers?: { aggregate?: { count?: number } };
  }) => {
    const active = countsData?.activeUsers?.aggregate?.count ?? 0;
    const deactivated = countsData?.deactivatedUsers?.aggregate?.count ?? 0;
    const deleted = countsData?.deletedUsers?.aggregate?.count ?? 0;
    setUserCounts({
      active,
      deactivated,
      deleted,
      all: active + deactivated + deleted,
    });
  };

  const { data: activeSuperUsersData, refetch: refetchActiveSuperUsers } = useQuery<{
    super_user: { u_id: string }[];
  }>(GET_ACTIVE_SUPER_USER_USER_IDS, {
    skip: !canManageSuperUserTable,
    fetchPolicy: 'network-only',
  });

  const activeSuperUserIdSet = useMemo(() => {
    const rows = activeSuperUsersData?.super_user;
    const base = new Set(rows?.map((r) => r.u_id) || []);
    Object.entries(superUserToggleOverrides).forEach(([uId, isActive]) => {
      if (isActive) base.add(uId);
      else base.delete(uId);
    });
    return base;
  }, [activeSuperUsersData, superUserToggleOverrides]);

  const isSuperUserToggleChecked = (uId: string) => activeSuperUserIdSet.has(uId);

  const getPromotePercentageForUser = (user: User): number => {
    const feePercentage = Number(user.u_fee_percentage);
    const pricingMode = normalizeClientType(user.u_pricing_mode || user.u_client_type || 'none');
    const isSelfPricing = pricingMode === 'self-pricing';

    if (isSelfPricing) {
      if (Number.isFinite(feePercentage) && feePercentage > 0 && feePercentage !== 20) {
        return feePercentage;
      }
      return 15;
    }

    if (Number.isFinite(feePercentage) && feePercentage > 0) {
      return feePercentage;
    }
    return 20;
  };

  const handleSuperUserTableToggle = async (user: User, nextChecked: boolean) => {
    if (!canManageSuperUserTable) return;
    const pricing = normalizeClientType(user.u_pricing_mode || user.u_client_type || "none");
    if (pricing !== "self-pricing") return;

    const canEditRow = user.u_is_active && !user.u_delete_requested;
    if (!canEditRow) return;

    setSuperUserToggleUserId(user.u_id);
    try {
      if (nextChecked) {
        await callNodeApi("/api/promoteUserToSuperUser", {
          input: {
            promoteUserToSuperUserInput: {
              emailId: user.u_email_id,
              percentage: getPromotePercentageForUser(user),
            },
          },
        });
        success("Super user access enabled");
      } else {
        await callNodeApi("/api/deactivateSuperUser", {
          input: {
            deactivateSuperUserInput: { emailId: user.u_email_id },
          },
        });
        success("Super user access removed");
      }
      setSuperUserToggleOverrides((prev) => ({ ...prev, [user.u_id]: nextChecked }));
      try {
        const refetchResult = await refetchActiveSuperUsers();
        if (refetchResult?.data?.super_user) {
          setSuperUserToggleOverrides((prev) => {
            const next = { ...prev };
            delete next[user.u_id];
            return next;
          });
        }
      } catch {
        // Non-blocking: role needs Hasura select on super_user (see public_super_user.yaml)
      }
      try {
        await refetch();
      } catch {
        // Non-blocking list refresh
      }
    } catch (e: any) {
      showError(e?.message || "Super user update failed");
    } finally {
      setSuperUserToggleUserId(null);
    }
  };

  const leagueMap = React.useMemo(() => {
    const map = new Map<string, string>();
    if (allLeaguesData?.leagues) {
      allLeaguesData.leagues.forEach((league) => {
        if (league?.l_id && league.l_name) {
          map.set(league.l_id, league.l_name);
        }
      });
    }
    return map;
  }, [allLeaguesData]);

  // Build team options from canonical league categories and already assigned user teams.
  const teamsList = React.useMemo(() => {
    const teamMap = new Map<string, TeamOption>();

    if (availableTeamsData?.league_categories) {
      availableTeamsData.league_categories.forEach((team) => {
        if (!team?.lc_name) return;
        const label = team.lc_abbrev ? `${team.lc_name} (${team.lc_abbrev})` : team.lc_name;
        teamMap.set(team.lc_id, {
          value: team.lc_id,
          label,
          leagueId: team.l_id,
          leagueName: leagueMap.get(team.l_id || '') || undefined,
          categoryId: team.lc_id,
        });
      });
    }

    return Array.from(teamMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [availableTeamsData, leagueMap]);

  const selectedTeamLabel = React.useMemo(() => {
    if (!selectedTeam) return '';
    const found = teamsList.find(t => t.value === selectedTeam);
    if (found) return found.label;
    const lower = String(selectedTeam).toLowerCase();
    const fuzzy = teamsList.find(t => t.value.toLowerCase() === lower || t.label.toLowerCase() === lower);
    return fuzzy ? fuzzy.label : selectedTeam;
  }, [selectedTeam, teamsList]);

  const filteredTeamsList = React.useMemo(() => {
    const query = teamFilterSearch.trim().toLowerCase();
    if (!query) return teamsList;
    return teamsList.filter((team) =>
      team.label.toLowerCase().includes(query) || team.value.toLowerCase().includes(query)
    );
  }, [teamFilterSearch, teamsList]);

  // Process sales reps data
  const allSalesRepsList = React.useMemo(() => {
    return salesRepsData?.salesReps ?? [];
  }, [salesRepsData]);

  const activeSalesRepsList = React.useMemo(() => {
    return allSalesRepsList.filter(rep => rep.sr_is_active !== false);
  }, [allSalesRepsList]);

  const filteredSalesReps = React.useMemo(() => {
    const q = salesRepSearchTerm.trim().toLowerCase();
    if (!q) return activeSalesRepsList;
    return activeSalesRepsList.filter((rep) => {
      const name = `${rep.sr_first_name || ''} ${rep.sr_last_name || ''}`.trim().toLowerCase();
      const email = (rep.sr_email || '').toLowerCase();
      return name.includes(q) || email.includes(q) || (rep.sr_id || '').toLowerCase().includes(q);
    });
  }, [salesRepSearchTerm, activeSalesRepsList]);

  const loggedInSalesRep = React.useMemo(() => {
    if (!isMarketingUser || !loggedInEmail) return null;
    return activeSalesRepsList.find(
      (rep) => (rep.sr_email || "").toLowerCase().trim() === loggedInEmail
    ) || null;
  }, [activeSalesRepsList, isMarketingUser, loggedInEmail]);

  const scopedSalesRepId = loggedInSalesRep?.sr_id || null;

  const salesRepsList = React.useMemo(() => {
    if (!isMarketingUser) return activeSalesRepsList;
    return loggedInSalesRep ? [loggedInSalesRep] : [];
  }, [activeSalesRepsList, isMarketingUser, loggedInSalesRep]);

  useEffect(() => {
    if (!isMarketingUser || !scopedSalesRepId) return;
    setNewUser((prev) =>
      prev.sales_rep_id === scopedSalesRepId ? prev : { ...prev, sales_rep_id: scopedSalesRepId }
    );
  }, [isMarketingUser, scopedSalesRepId]);

  const teamOptionsForFilter = React.useMemo(() => {
    if (!availableTeamsData?.league_categories) return [];
    return availableTeamsData.league_categories
      .filter((team) => team?.lc_name)
      .map((team) => ({
        value: team.lc_id,
        label: team.lc_abbrev ? `${team.lc_name} (${team.lc_abbrev})` : team.lc_name,
      }));
  }, [availableTeamsData]);

  const usersListFilterArgs = React.useMemo(
    () => ({
      searchText: debouncedSearch,
      clientType,
      selectedTeam,
      teamsList: teamOptionsForFilter,
      scopedSalesRepId,
      scopedAffiliateId,
      isMarketingUser,
      isAffiliateAdmin,
      lastActiveRange,
      memberSinceRange,
      normalizeClientType,
    }),
    [
      debouncedSearch,
      clientType,
      selectedTeam,
      teamOptionsForFilter,
      scopedSalesRepId,
      scopedAffiliateId,
      isMarketingUser,
      isAffiliateAdmin,
      lastActiveRange,
      memberSinceRange,
    ]
  );

  const usersListTabCountsWhere = React.useMemo(
    () =>
      buildUsersListScopeWhere({
        scopedSalesRepId,
        scopedAffiliateId,
        isMarketingUser,
        isAffiliateAdmin,
      }),
    [scopedSalesRepId, scopedAffiliateId, isMarketingUser, isAffiliateAdmin]
  );

  const usersListWhere = React.useMemo(
    () =>
      buildUsersListWhere({
        ...usersListFilterArgs,
        statusFilter: activeFilter as 'active' | 'deactivated' | 'deleted' | 'all',
      }),
    [usersListFilterArgs, activeFilter]
  );

  const usersListOrderBy = React.useMemo(
    () => buildUsersListOrderBy(sortColumn, sortDirection),
    [sortColumn, sortDirection]
  );

  const { refetch: refetchCounts } = useQuery(GET_USER_COUNTS, {
    variables: { baseWhere: usersListTabCountsWhere },
    fetchPolicy: 'cache-first',
    onCompleted: applyAggregateUserCounts,
  });

  const { loading, error, data, refetch } = useQuery(GET_USERS_LIST_FILTERED, {
    variables: {
      where: usersListWhere,
      pageSize,
      pageOffset,
      order_by: usersListOrderBy,
    },
    fetchPolicy: 'network-only',
    onError: (err) => {
      showError(err.message);
    },
  });

  const userIds = React.useMemo(() => {
    if (!Array.isArray(data?.user)) return [];
    return data.user.map((user: User) => user.u_id);
  }, [data]);

  const { data: usersPreferenceData, refetch: refetchUsersPreferenceTeams } = useQuery<{
    user_preference: Array<{
      u_id: string;
      lc_id: string;
      league_category?: { lc_id?: string; lc_name?: string; lc_abbrev?: string };
    }>;
  }>(GET_USERS_PREFERENCE_TEAMS, {
    variables: { userIds },
    skip: userIds.length === 0,
    fetchPolicy: 'network-only',
  });

  const userPreferenceTeamsByUserId = React.useMemo(() => {
    const map = new Map<string, string[]>();
    if (usersPreferenceData && Array.isArray(usersPreferenceData.user_preference)) {
      usersPreferenceData.user_preference.forEach((pref) => {
        const teamName = (pref?.league_category?.lc_name || pref?.league_category?.lc_abbrev || '').trim();
        if (!teamName) return;
        const existing = map.get(pref.u_id) || [];
        existing.push(teamName);
        map.set(pref.u_id, existing);
      });
    }
    return map;
  }, [usersPreferenceData]);

  useEffect(() => {
    if (!data?.user) return;
    setFilteredUsers(data.user);
    setTotalCount(data.user_aggregate?.aggregate?.count ?? 0);
  }, [data]);

  // Helper function to check if a date is within range
  const isDateInRange = (dateString: string, range: DateRange): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;

    if (range.from && range.to) {
      return date >= range.from && date <= range.to;
    }
    if (range.from) {
      return date >= range.from;
    }
    if (range.to) {
      return date <= range.to;
    }
    return true;
  };

  const compareValues = (a: unknown, b: unknown, direction: 'asc' | 'desc') => {
    const isMissing = (value: unknown) =>
      value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value));

    if (isMissing(a)) {
      if (isMissing(b)) return 0;
      return 1;
    }
    if (isMissing(b)) {
      return -1;
    }

    const numA = typeof a === 'string' && a.trim() !== '' && !Number.isNaN(Number(a)) ? Number(a) : a;
    const numB = typeof b === 'string' && b.trim() !== '' && !Number.isNaN(Number(b)) ? Number(b) : b;

    if (typeof numA === 'number' && typeof numB === 'number') {
      return direction === 'asc' ? numA - numB : numB - numA;
    }

    const strA = String(numA).toLowerCase();
    const strB = String(numB).toLowerCase();

    if (strA < strB) return direction === 'asc' ? -1 : 1;
    if (strA > strB) return direction === 'asc' ? 1 : -1;
    return 0;
  };

  const getStatusIcon = (user: User) => {
    if (user.u_delete_requested) {
      return <XCircle size={14} className={styles.statusIconDeleted} />;
    }
    return user.u_is_active ? 
      <CheckCircle size={14} className={styles.statusIconActive} /> : 
      <XCircle size={14} className={styles.statusIconDeactivated} />;
  };

  const getStatusText = (user: User) => {
    if (user.u_delete_requested) return 'Deleted';
    return user.u_is_active ? 'Active' : 'Deactivated';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'none';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'none';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2, // Show at least 2 decimal places
    maximumFractionDigits: 6, // Show up to 6 decimal places for precision
  }).format(amount || 0);
};
  const getActiveTickets = (user: User) => {
    return user.tickets_aggregate?.aggregate?.sum?.t_active_tickets || 0;
  };

  const getTotalSold = (user: User) => {
    return user.tickets_aggregate?.aggregate?.sum?.t_sold_tickets || 0;
  };

  const getTotalListed = (user: User) => {
    return getActiveTickets(user) + getTotalSold(user);
  };

  const getTotalRevenue = (user: User) => {
    return user.tickets_sold_amount || 0;
  };

  const getActiveTicketListPriceTotal = (user: User) => {
    if (!Array.isArray(user.tickets)) return 0;
    return user.tickets.reduce((sum, ticket) => {
      return (
        sum +
        (ticket?.ticket_placements_aggregate?.aggregate?.sum?.tp_list_price || 0)
      );
    }, 0);
  };

const getPotentialPayoutValue = (user: User) => {
  const activeTicketListValue = getActiveTicketListPriceTotal(user);
  const feePercent = getPromotePercentageForUser(user);
  if (!Number.isFinite(feePercent) || feePercent <= 0) {
    return activeTicketListValue; // Return exact value, not rounded
  }
  return activeTicketListValue * (100 - feePercent) / 100; // Return exact value, not rounded
};
  const getPotentialPayout = (user: User) => {
  return formatCurrency(getPotentialPayoutValue(user));
};
  const getClientType = (user: User) => {
    return user.u_pricing_mode || user.u_client_type || 'none';
  };

  const getSellerTier = (user: User) => {
    return user.u_seller_tier || 'none';
  };

  const getTeamDisplayNameFromId = (teamId: string): string => {
    const team = teamsList.find((t) => t.value === teamId);
    if (!team?.label) return teamId;
    const match = team.label.match(/^(.+?)\s*\(/);
    return (match ? match[1] : team.label).trim();
  };

  const getBaseUserTeamNames = (user: User): string[] => {
    const fromPreferenceMap = userPreferenceTeamsByUserId.get(user.u_id);
    if (fromPreferenceMap?.length) return fromPreferenceMap;

    if (Array.isArray(user.user_preference) && user.user_preference.length) {
      return user.user_preference
        .map((pref) => pref?.league_category?.lc_name)
        .filter((name): name is string => Boolean(name));
    }

    if (Array.isArray(user.u_teams) && user.u_teams.length) return user.u_teams;
    return [];
  };

  const getUserTeamNamesResolved = (user: User): string[] => {
    const optimistic = optimisticTeamNamesByUserId[user.u_id];
    if (optimistic) return optimistic;
    return getBaseUserTeamNames(user);
  };

  const getUserTeams = (user: User) => {
    const names = getUserTeamNamesResolved(user);
    return names.length > 0 ? names.join(', ') : 'None';
  };

  const getUserTeamNames = (user: User) => getUserTeamNamesResolved(user);

  const getSalesRep = (user: User) => {
    if (!user.u_sales_reps_id) return 'none';
    const rep = allSalesRepsList.find(sr => sr.sr_id === user.u_sales_reps_id);
    if (!rep) return 'none';
    return `${rep.sr_first_name || ''} ${rep.sr_last_name || ''}`.trim() || rep.sr_email || 'none';
  };

  const getAffiliate = (user: User): string => {
    return getAffiliateDisplayName(user);
  };

  const getSortValue = (user: User, column: string) => {
    switch (column) {
      case 'name':
        return user.u_full_name?.trim() || `${user.u_first_name || ''} ${user.u_last_name || ''}`.trim();
      case 'email':
        return user.u_email_id?.trim() || '';
      case 'status':
        return getStatusText(user);
      case 'clientType':
        return getClientType(user);
      case 'salesRep':
        return getSalesRep(user);
      case 'affiliate':
        return getAffiliate(user);
      case 'teams':
        return getUserTeams(user);
      case 'lastActive':
        return user.u_last_online ? new Date(user.u_last_online).getTime() : null;
      case 'memberSince':
        return user.u_created_at ? new Date(user.u_created_at).getTime() : null;
      case 'activeTickets':
        return getActiveTickets(user);
      case 'totalListed':
        return getTotalListed(user);
      case 'potentialPayout':
        return getPotentialPayoutValue(user);
      case 'totalSold':
        return getTotalSold(user);
      case 'totalRevenue':
        return getTotalRevenue(user);
      default:
        return '';
    }
  };

const SERVER_SORT_COLUMNS = new Set([
  'name',
  'email',
  'status',
  'clientType',
  'salesRep',
  'affiliate',
  'lastActive',
  'memberSince',
  'totalSold',
  'totalRevenue',
]);

const sortedUsers = useMemo(() => {
  if (!sortColumn || SERVER_SORT_COLUMNS.has(sortColumn)) {
    return filteredUsers;
  }
  return [...filteredUsers].sort((first, second) => {
    const firstValue = getSortValue(first, sortColumn);
    const secondValue = getSortValue(second, sortColumn);
    return compareValues(firstValue, secondValue, sortDirection);
  });
}, [filteredUsers, sortColumn, sortDirection]);

  const handleSortColumn = (column: string) => {
    setPageOffset(0);
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection(DATE_SORT_COLUMNS.has(column) ? 'desc' : 'asc');
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? <ChevronUp size={14} className={styles.sortIcon} /> : <ChevronDown size={14} className={styles.sortIcon} />;
  };

  const getSortAria = (column: string) => {
    if (sortColumn !== column) return 'none';
    return sortDirection === 'asc' ? 'ascending' : 'descending';
  };

  // Helper function to get affiliate display name
  function getAffiliateDisplayName(user: User): string {
    if (user.u_affiliate_id) {
      const affiliate = affiliatesMap.get(user.u_affiliate_id);
      if (affiliate) {
        return affiliate.u_full_name;
      }
      if (user.affiliate) {
        const affiliateAny = user.affiliate as any;
        // First try to use the user object within affiliate (most reliable)
        if (affiliateAny?.user?.u_full_name) {
          return affiliateAny.user.u_full_name;
        }
        // Fallback to affiliate user details
        const nameFromAffiliateRelation =
          affiliateAny?.user?.u_full_name ||
          `${affiliateAny?.user?.u_first_name || ""} ${affiliateAny?.user?.u_last_name || ""}`.trim() ||
          `${affiliateAny?.a_first_name || ""} ${affiliateAny?.a_last_name || ""}`.trim() ||
          affiliateAny?.a_email_id ||
          affiliateAny?.user?.u_email_id;
        if (nameFromAffiliateRelation) {
          return nameFromAffiliateRelation;
        }
      }
      return 'Unknown Affiliate';
    }
    return 'None';
  }

  // Mutations
  const [updateUserStatus] = useMutation(UPDATE_USER_STATUS, {
    onCompleted: () => {
      success('User status updated successfully');
      refetch();
      refetchCounts();
    },
    onError: (err) => showError(err.message)
  });

  const [addUserPreferenceTeamMutation] = useMutation(ADD_USER_PREFERENCE_TEAM, {
    onError: (err) => showError(err.message)
  });

  const [removeUserPreferenceTeamMutation] = useMutation(REMOVE_USER_PREFERENCE_TEAM, {
    onError: (err) => showError(err.message)
  });

  const [clearUserPreferenceTeamsMutation] = useMutation(CLEAR_USER_PREFERENCE_TEAMS, {
    onError: (err) => showError(err.message)
  });

  const [setUserTeamsMutation] = useMutation(SET_USER_TEAMS, {
    onCompleted: () => {
      refetch();
    },
    onError: (err) => showError(err.message)
  });

  const [updateSellerTierMutation] = useMutation(UPDATE_SELLER_TIER, {
    onError: (err) => showError(err.message)
  });

  const [softDeleteUserMutation] = useMutation(SOFT_DELETE_USER, {
    onCompleted: (data: any) => {
      if (data?.update_user?.affected_rows > 0) {
        const deletedUserId = data?.update_user?.returning?.[0]?.u_id;
        if (deletedUserId) {
          setFilteredUsers(prev => prev.filter(u => u.u_id !== deletedUserId));
          setTotalCount(prev => Math.max(prev - 1, 0));
          setUserCounts(prev => ({
            ...prev,
            all: Math.max(prev.all - 1, 0),
            deleted: prev.deleted + 1
          }));
        }
        success('User deleted successfully');
        refetchCounts();
        refetch();
      } else {
        showError('User could not be deleted');
      }
    },
    onError: (err) => {
      showError(err.message || 'Failed to delete user');
    }
  });

  const [adminCreateUserMutation] = useMutation(ADMIN_CREATE_USER);
  const [reactivateUserMutation] = useMutation(REACTIVATE_USER, {
    onError: (err) => showError(err.message)
  });

  // Get paginated users
  const paginatedUsers = sortedUsers;
  const actionMenuUser = openActionMenu
    ? paginatedUsers.find((user) => user.u_id === openActionMenu) ?? null
    : null;

  const filters = [
    { id: 'active', label: 'Active', count: userCounts.active },
    { id: 'deactivated', label: 'Deactivated', count: userCounts.deactivated },
    { id: 'deleted', label: 'Deleted', count: userCounts.deleted },
    { id: 'all', label: 'All', count: userCounts.all }
  ];

  const clientTypes = ['All', 'concierge', 'self-pricing'];
  
  const resetNewUserForm = () => {
    setNewUser({
      first_name: "",
      last_name: "",
      business_name: "",
      email: "",
      phone: "",
      country_code: "+1",
      client_type: "concierge",
      seller_tier: "",
      sales_rep_id: isMarketingUser && scopedSalesRepId ? scopedSalesRepId : "",
      affiliate_id: isAffiliateAdmin && scopedAffiliateId ? scopedAffiliateId : "",
      selected_teams: [],
      password: "",
      confirmPassword: "",
      super_user_percentage: defaultConciergePercentage,
    });
    setFormErrors({});
    setAddUserLoading(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const validatePhoneNumber = (value: string) => /^\d{10}$/.test(value.replace(/\D/g, ''));

  const validateCountryCode = (value: string) => /^\+\d{1,4}$/.test(value.trim());

  const validatePasswordValue = (value: string) => {
    const hasSpecialCharacter = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value);
    return value.length >= 10 && hasSpecialCharacter;
  };

  const validateForm = (): boolean => {
    const errors: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      country_code?: string;
      password?: string;
      confirmPassword?: string;
      super_user_percentage?: string;
    } = {};

    if (!newUser.first_name.trim()) {
      errors.first_name = "First name is required";
    }

    if (!newUser.last_name.trim()) {
      errors.last_name = "Last name is required";
    }

    if (!newUser.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(newUser.email)) {
      errors.email = "Invalid email format";
    }

    if (!newUser.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!validatePhoneNumber(newUser.phone)) {
      errors.phone = "Phone number must be exactly 10 digits";
    }

    if (!newUser.country_code.trim()) {
      errors.country_code = "Country code is required";
    } else if (!validateCountryCode(newUser.country_code)) {
      errors.country_code = "Invalid country code";
    }

    if (!newUser.password) {
      errors.password = "Password is required";
    } else if (!validatePasswordValue(newUser.password)) {
      errors.password = "Password must be at least 10 characters and contain one special character";
    }

    if (!newUser.confirmPassword) {
      errors.confirmPassword = "Confirm password is required";
    } else if (newUser.password !== newUser.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (!newUser.super_user_percentage.trim()) {
      errors.super_user_percentage = "Percentage is required";
    } else {
      const percentage = Number(newUser.super_user_percentage);
      if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
        errors.super_user_percentage = "Percentage must be greater than 0 and at most 100";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddUser = async () => {
    if (!validateForm()) return;
    const effectiveSalesRepId = isMarketingUser ? scopedSalesRepId : (newUser.sales_rep_id || null);
    if (isMarketingUser && !effectiveSalesRepId) {
      showError('Unable to find your sales rep account. Please refresh and try again.');
      return;
    }
    const effectiveAffiliateId = isAffiliateAdmin ? scopedAffiliateId : (newUser.affiliate_id || null);
    if (isAffiliateAdmin && !effectiveAffiliateId) {
      showError('Unable to find your affiliate account. Please refresh and try again.');
      return;
    }

    const firstName = newUser.first_name.trim();
    const lastName = newUser.last_name.trim();
    const email = newUser.email.trim().toLowerCase();
    const feePercentage = Number(newUser.super_user_percentage);

    const matchingExistingUser = Array.isArray(data?.user)
      ? data.user.find((user: User) => (user.u_email_id || '').toLowerCase() === email)
      : undefined;

    if (matchingExistingUser && matchingExistingUser.u_is_active && !matchingExistingUser.u_delete_requested) {
      showError('User already exists with this email.');
      return;
    }

    setAddUserLoading(true);

    const shouldReactivateExistingUser = Boolean(
      matchingExistingUser &&
      (!matchingExistingUser.u_is_active || matchingExistingUser.u_delete_requested)
    );

    const isDuplicateTeamAssignmentError = (error: any): boolean => {
      const graphQlMessage = error?.graphQLErrors?.[0]?.message || '';
      const message = (error?.message || graphQlMessage || '').toLowerCase();
      return message.includes('duplicate key') || message.includes('already exists');
    };

    const assignPostCreateFields = async (createdUserId: string) => {
      const isUnsupportedOptionalFieldError = (error: any): boolean => {
        const graphQlMessage = error?.graphQLErrors?.[0]?.message || '';
        const message = (error?.message || graphQlMessage || '').toLowerCase();
        return (
          message.includes('not found in type') ||
          message.includes('is not defined by type') ||
          message.includes('cannot query field')
        );
      };

      // Update sales rep
      if (effectiveSalesRepId) {
        try {
          await assignSalesRepViaApi(createdUserId, effectiveSalesRepId);
        } catch (error) {
          if (!isUnsupportedOptionalFieldError(error)) {
            throw error;
          }
        }
      }

      // Update affiliate
      if (effectiveAffiliateId) {
        try {
          await assignAffiliateViaApi(createdUserId, effectiveAffiliateId);
        } catch (error) {
          if (!isUnsupportedOptionalFieldError(error)) {
            throw error;
          }
        }
      }

      // Assign teams
      if (newUser.selected_teams.length > 0) {
        try {
          await setUserTeamsMutation({
            variables: { userId: createdUserId, teams: newUser.selected_teams }
          });
        } catch (teamError) {
          // ignore team assignment errors
        }
      }
    };

    const shouldFallbackToNodeApiCreate = (error: any): boolean => {
      const graphQlMessage = error?.graphQLErrors?.[0]?.message || '';
      const message = (error?.message || graphQlMessage || '').toLowerCase();
      return (
        message.includes("cannot query field 'admincreateuser'") ||
        message.includes("field 'admincreateuser' not found") ||
        message.includes('unknown type "admincreateuserinput"') ||
        message.includes("is not defined by type 'admincreateuserinput'") ||
        message.includes('is not defined by type "admincreateuserinput"') ||
        message.includes('argument "input" has invalid value') ||
        message.includes('query is not allowed') ||
        message.includes('mutation is not allowed') ||
        message.includes('allowlist')
      );
    };

    const createUserViaNodeApi = async (): Promise<{ userId: string | null; message: string }> => {
      const responseData = await callNodeApi("/api/staffCreateUser", {
        input: {
          firstName,
          lastName,
          emailId: email,
          phoneNumber: newUser.phone.trim(),
          countryCode: newUser.country_code.trim(),
          clientType: newUser.client_type,
          password: newUser.password,
          percentage: feePercentage,
          businessName: newUser.business_name?.trim() || null,
          salesRepId: effectiveSalesRepId,
          affiliateId: effectiveAffiliateId,
          teams: newUser.selected_teams,
        },
      });

      return {
        userId: responseData?.user?.u_id || null,
        message: responseData?.message || "User created successfully",
      };
    };

    try {
      let createdUserId: string | null = null;
      let createUserMessage = "User created successfully";

      if (shouldReactivateExistingUser && matchingExistingUser) {
        const reactivateResponse = await reactivateUserMutation({
          variables: {
            userId: matchingExistingUser.u_id,
            input: {
              u_first_name: firstName,
              u_last_name: lastName,
              u_phone_number: newUser.phone.trim(),
              u_country_code: newUser.country_code.trim(),
              u_pricing_mode: newUser.client_type,
              u_client_type: newUser.client_type,
              u_fee_percentage: feePercentage,
              u_seller_tier: newUser.seller_tier || null,
              u_sales_reps_id: effectiveSalesRepId,
              u_affiliate_id: effectiveAffiliateId,
              u_is_active: true,
              u_delete_requested: false,
              u_stripe_business_name: newUser.business_name?.trim() || null
            }
          }
        });

        createdUserId = reactivateResponse?.data?.update_user?.returning?.[0]?.u_id || matchingExistingUser.u_id;
        createUserMessage = 'User reactivated successfully';
      } else {
        try {
          const backendResponse = await adminCreateUserMutation({
            variables: {
              input: {
                firstName,
                lastName,
                emailId: email,
                phoneNumber: newUser.phone.trim(),
                countryCode: newUser.country_code.trim(),
                clientType: newUser.client_type,
                password: newUser.password,
                percentage: feePercentage,
                businessName: newUser.business_name?.trim() || null,
                salesRepId: effectiveSalesRepId,
                affiliateId: effectiveAffiliateId,
                teams: newUser.selected_teams
              }
            }
          });

          createdUserId = backendResponse?.data?.adminCreateUser?.user?.u_id || null;
          createUserMessage = backendResponse?.data?.adminCreateUser?.message || createUserMessage;
        } catch (backendError: any) {
          if (!shouldFallbackToNodeApiCreate(backendError)) {
            throw backendError;
          }
          const nodeCreateResult = await createUserViaNodeApi();
          createdUserId = nodeCreateResult.userId;
          createUserMessage = nodeCreateResult.message || createUserMessage;
        }
      }

      if (!createdUserId) {
        throw new Error('User creation response is missing user id');
      }

      await assignPostCreateFields(createdUserId);

      success(createUserMessage || 'User added successfully');
      refetch();
      refetchCounts();
      setShowAddDialog(false);
      resetNewUserForm();
    } catch (error: any) {
      console.error('Add user error:', error);
      const graphQlMessage = error?.graphQLErrors?.[0]?.message || '';
      const rawMessage = error?.message || graphQlMessage || '';
      const normalized = rawMessage.toLowerCase();

      if (
        normalized.includes('user_u_email_id_key') ||
        normalized.includes('duplicate key value violates unique constraint') ||
        normalized.includes('user already exists')
      ) {
        showError('User already exists with this email.');
      } else {
        showError(rawMessage || 'Failed to add user');
      }
    } finally {
      setAddUserLoading(false);
    }
  };



  const handleExport = async () => {
    setIsExporting(true);
    try {
      const exportLimit = Math.min(Math.max(totalCount, pageSize), 10000);
      const exportResult = await apolloClient.query({
        query: GET_USERS_LIST_FILTERED,
        variables: {
          where: usersListWhere,
          pageSize: exportLimit,
          pageOffset: 0,
          order_by: usersListOrderBy,
        },
        fetchPolicy: 'network-only',
      });
      const exportUsers: User[] = exportResult.data?.user ?? [];
      const exportUserIds = exportUsers.map((user) => user.u_id);
      let exportPreferenceMap = new Map<string, string[]>();

      if (exportUserIds.length > 0) {
        const prefResult = await apolloClient.query({
          query: GET_USERS_PREFERENCE_TEAMS,
          variables: { userIds: exportUserIds },
          fetchPolicy: 'network-only',
        });
        if (Array.isArray(prefResult.data?.user_preference)) {
          prefResult.data.user_preference.forEach((pref: {
            u_id: string;
            league_category?: { lc_name?: string; lc_abbrev?: string };
          }) => {
            const teamName = (pref?.league_category?.lc_name || pref?.league_category?.lc_abbrev || '').trim();
            if (!teamName) return;
            const existing = exportPreferenceMap.get(pref.u_id) || [];
            existing.push(teamName);
            exportPreferenceMap.set(pref.u_id, existing);
          });
        }
      }

      const getExportUserTeams = (user: User) => {
        const names = exportPreferenceMap.get(user.u_id) || user.u_teams || [];
        return names.length > 0 ? names.join(', ') : 'None';
      };

      const exportData = exportUsers.map(user => ({
        'Name': user.u_full_name || `${user.u_first_name} ${user.u_last_name}`,
        'Email': user.u_email_id,
        'Status': user.u_delete_requested ? 'Deleted' : (user.u_is_active ? 'Active' : 'Deactivated'),
        'Client Type': user.u_pricing_mode || 'none',
        'Seller Tier': user.u_seller_tier || 'none',
        'Teams': getExportUserTeams(user),
        'Sales Rep': (() => {
          const rep = allSalesRepsList.find(sr => sr.sr_id === user.u_sales_reps_id);
          return rep ? `${rep.sr_first_name || ''} ${rep.sr_last_name || ''}`.trim() || rep.sr_email : 'none';
        })(),
        'Affiliate': getAffiliateDisplayName(user),
        'Last Active': user.u_last_online ? new Date(user.u_last_online).toLocaleString() : 'none',
        'Member Since': user.u_created_at ? new Date(user.u_created_at).toLocaleDateString() : 'none',
        'Active Tickets': user.tickets_aggregate?.aggregate?.sum?.t_active_tickets || 0,
        'Total Sold': user.tickets_aggregate?.aggregate?.sum?.t_sold_tickets || 0,
        'Total Revenue': user.tickets_sold_amount ? `$${user.tickets_sold_amount.toLocaleString()}` : '$0'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Users');
      
      const date = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `users_export_${date}.xlsx`);
      
      success('Export completed successfully!');
    } catch (err) {
      showError('Failed to export users');
    } finally {
      setIsExporting(false);
    }
  };

  const handleFullRefresh = async () => {
    if (loading || isRefreshing) return;
    setIsRefreshing(true);
    try {
      const refreshTasks: Array<Promise<unknown>> = [
        refetch(),
        refetchUsersPreferenceTeams(),
        refetchCounts(),
      ];
      if (canReadSalesReps) {
        refreshTasks.push(refetchSalesReps());
      }
      if (shouldFetchAffiliates) {
        refreshTasks.push(refetchAffiliates());
      }
      if (shouldLoadAvailableTeams) {
        refreshTasks.push(refetchAvailableTeams());
      }
      if (canManageSuperUserTable) {
        refreshTasks.push(refetchActiveSuperUsers());
      }
      await Promise.all(refreshTasks);
    } catch (err) {
      console.error('Users refresh failed:', err);
      showError('Failed to refresh users');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefresh = () => {
    void handleFullRefresh();
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageOffset(0);
  };

  const handlePageChange = (newOffset: number) => {
    setPageOffset(newOffset);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPageOffset(0);
  };

  const handleStatusFilter = (filterId: string) => {
    setActiveFilter(filterId);
    setPageOffset(0);
    navigate(`/users?status_filter=${filterId}`);
  };

  const handleClientTypeChange = (type: string) => {
    if (type === 'All') {
      setClientType(null);
    } else {
      setClientType(type);
    }
    setShowClientTypeFilter(false);
    setPageOffset(0);
  };

  const handleTeamFilter = (team: string) => {
    setSelectedTeam(prev => prev === team ? "" : team);
    setShowTeamFilter(false);
    setPageOffset(0);
  };

  const clearTeamFilters = () => {
    setSelectedTeam("");
    setPageOffset(0);
  };

  // Date filter clear functions
  const clearLastActiveFilter = () => {
    setLastActiveRange({ from: null, to: null });
    setPageOffset(0);
  };

  const clearMemberSinceFilter = () => {
    setMemberSinceRange({ from: null, to: null });
    setPageOffset(0);
  };

  const clearAllFilters = () => {
    setActiveFilter('all');
    setClientType(null);
    setSelectedTeam("");
    setSelectedSalesRep(null);
    setSelectedAffiliate(null);
    setSearchQuery('');
    setLastActiveRange({ from: null, to: null });
    setMemberSinceRange({ from: null, to: null });
    setActiveDateFilter(null);
    setPageOffset(0);
    navigate('/users');
  };

  const hasActiveFilters = () => {
    return activeFilter !== 'all' || 
           clientType !== null || 
           selectedTeam !== "" || 
           selectedSalesRep !== null || 
           selectedAffiliate !== null ||
           searchQuery !== '' ||
           lastActiveRange.from !== null ||
           lastActiveRange.to !== null ||
           memberSinceRange.from !== null ||
           memberSinceRange.to !== null;
  };

  const toggleActionMenu = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (openActionMenu === userId) {
      closeActionMenu();
      return;
    }
    const button = e.currentTarget as HTMLButtonElement;
    dropdownAnchorRefs.current.set(`action-${userId}`, button);
    setActionMenuPos(computeActionMenuPos(button));
    setOpenActionMenu(userId);
  };

  const handleViewDetails = (user: User, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/user/${user.u_id}`);
    closeActionMenu();
  };

  const handleViewAnalytics = (user: User, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/user/${user.u_id}?tab=analytics`);
    closeActionMenu();
  };

  const handleUpdateUserStatus = async (user: User, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await updateUserStatus({
        variables: {
          userIds: [user.u_id],
          isActive: !user.u_is_active,
          isDeleted: false
        }
      });
    } catch (error) {
      showError('Failed to update user status');
    }
    closeActionMenu();
  };

  const handleDeleteUser = (user: User, e: React.MouseEvent) => {
    e.stopPropagation();
    closeActionMenu();
    setPendingDeleteUser(user);
  };

  const confirmDeleteUser = async () => {
    if (!pendingDeleteUser) return;
    try {
      await softDeleteUserMutation({
        variables: { userId: pendingDeleteUser.u_id }
      });
    } catch (error: any) {
      showError(error.message || 'Failed to delete user');
    } finally {
      setPendingDeleteUser(null);
    }
  };

  const refreshUserTeamsData = async (userId?: string) => {
    const tasks: Promise<unknown>[] = [refetchUsersPreferenceTeams(), refetch()];
    if (userId && showTeamsDropdown === userId) {
      tasks.push(refetchTeamsWithSoldStatus());
    }
    await Promise.all(tasks);
    if (userId) {
      setOptimisticTeamNamesByUserId((prev) => {
        if (!prev[userId]) return prev;
        const { [userId]: _removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const addUserTeam = async (userId: string, teamId: string) => {
    if (!teamId) return;
    const teamName = getTeamDisplayNameFromId(teamId);
    const user = filteredUsers.find((row) => row.u_id === userId);
    setOptimisticTeamNamesByUserId((prev) => {
      const current = prev[userId] ?? (user ? getBaseUserTeamNames(user) : []);
      if (current.includes(teamName)) return prev;
      return { ...prev, [userId]: [...current, teamName] };
    });
    try {
      await addUserPreferenceTeamMutation({ variables: { userId, teamId } });
      success('Team added successfully');
      await refreshUserTeamsData(userId);
    } catch (error) {
      setOptimisticTeamNamesByUserId((prev) => {
        if (!prev[userId]) return prev;
        const { [userId]: _removed, ...rest } = prev;
        return rest;
      });
      showError('Failed to add team');
    }
  };

  const removeUserTeam = async (userId: string, teamId: string) => {
    if (!teamId) return;
    const teamName = getTeamDisplayNameFromId(teamId);
    const user = filteredUsers.find((row) => row.u_id === userId);
    setOptimisticTeamNamesByUserId((prev) => {
      const current = prev[userId] ?? (user ? getBaseUserTeamNames(user) : []);
      return { ...prev, [userId]: current.filter((name) => name !== teamName) };
    });
    try {
      await removeUserPreferenceTeamMutation({ variables: { userId, teamId } });
      success('Team removed successfully');
      await refreshUserTeamsData(userId);
    } catch (error) {
      setOptimisticTeamNamesByUserId((prev) => {
        if (!prev[userId]) return prev;
        const { [userId]: _removed, ...rest } = prev;
        return rest;
      });
      showError('Failed to remove team');
    }
  };

  const toggleUserTeam = async (userId: string, currentTeamIds: string[], teamId: string) => {
    if (currentTeamIds.includes(teamId)) {
      await removeUserTeam(userId, teamId);
    } else {
      await addUserTeam(userId, teamId);
    }
  };

  const clearUserTeams = async (userId: string) => {
    setOptimisticTeamNamesByUserId((prev) => ({ ...prev, [userId]: [] }));
    try {
      await clearUserPreferenceTeamsMutation({ variables: { userId } });
      success('All teams cleared successfully');
      await refreshUserTeamsData(userId);
    } catch (error) {
      setOptimisticTeamNamesByUserId((prev) => {
        if (!prev[userId]) return prev;
        const { [userId]: _removed, ...rest } = prev;
        return rest;
      });
      showError('Failed to clear teams');
    }
    setShowTeamsDropdown(null);
  };
const nameRegex = /^[A-Za-z]+$/;
const alphaNumericRegex = /^[A-Za-z0-9]+$/;
  const updateUserSalesRep = async (userId: string, salesRepId: string) => {
    try {
      if (isMarketingUser && scopedSalesRepId && salesRepId && salesRepId !== scopedSalesRepId) {
        showError('You can only assign your own sales rep profile');
        return;
      }
      await assignSalesRepViaApi(userId, salesRepId || null);
      success('Sales rep updated successfully');
      await Promise.all([refetch(), refetchSalesReps()]);
    } catch (error) {
      showError('Failed to update sales rep');
    }
    setShowSalesRepDropdown(null);
  };

  const updateUserAffiliate = async (userId: string, affiliateId: string) => {
    try {
      await assignAffiliateViaApi(userId, affiliateId || null);
      await Promise.all([refetch(), refetchAffiliates()]);

      setFilteredUsers(prevUsers => prevUsers.map(user => {
        if (user.u_id === userId) {
          let affiliateObj: User['affiliate'] | undefined = undefined;
          if (affiliateId) {
            const found = affiliatesList.find(a => a.a_id === affiliateId);
            if (found) {
              affiliateObj = {
                a_id: found.a_id,
                a_first_name: found.a_first_name,
                a_last_name: found.a_last_name,
                a_email_id: found.a_email_id,
                user: found.u_id ? {
                  u_id: found.u_id,
                  u_full_name: found.u_full_name,
                  u_first_name: found.u_first_name || '',
                  u_last_name: found.u_last_name || '',
                  u_email_id: found.u_email_id || ''
                } : undefined
              };
            }
          }
          return {
            ...user,
            u_affiliate_id: affiliateId || null,
            affiliate: affiliateObj
          };
        }
        return user;
      }));
    } catch (error) {
      showError('Failed to update affiliate');
    }
    setShowAffiliateDropdown(null);
  };

  // Combined date filter component render
  const renderCombinedDateFilter = () => {
    const hasFilter = lastActiveRange.from !== null || lastActiveRange.to !== null || 
                      memberSinceRange.from !== null || memberSinceRange.to !== null;
    
    const getActiveFilterLabel = () => {
      if (activeDateFilter === 'lastActive') return 'Last Active';
      if (activeDateFilter === 'memberSince') return 'Member Since';
      return 'Date Filter';
    };
    
    return (
      <div className={styles.dropdown}>
        <button 
          className={`${styles.dropdownButton} ${hasFilter ? styles.activeFilter : ''}`}
          onClick={() => setShowDateFilterMenu(!showDateFilterMenu)}
        >
          <Filter size={14} />
          <span>{getActiveFilterLabel()}{hasFilter ? ' •' : ''}</span>
          <ChevronDown size={14} />
        </button>
        
        {showDateFilterMenu && (
          <div className={styles.dateFilterMainMenu}>
            <div className={styles.dateFilterHeader}>
              <span>Date Filter</span>
              {hasFilter && (
                <button onClick={() => {
                  clearLastActiveFilter();
                  clearMemberSinceFilter();
                  setActiveDateFilter(null);
                }} className={styles.clearDateBtn}>
                  Clear All
                </button>
              )}
            </div>
            
            {/* Filter Type Selection */}
            <div className={styles.filterTypeSelector}>
              <button 
                className={`${styles.filterTypeBtn} ${activeDateFilter === 'lastActive' ? styles.activeFilterType : ''}`}
                onClick={() => setActiveDateFilter('lastActive')}
              >
                Last Active
              </button>
              <button 
                className={`${styles.filterTypeBtn} ${activeDateFilter === 'memberSince' ? styles.activeFilterType : ''}`}
                onClick={() => setActiveDateFilter('memberSince')}
              >
                Member Since
              </button>
            </div>
            
            {/* Date Range Picker - Last Active */}
            {activeDateFilter === 'lastActive' && (
              <div className={styles.dateRangePanel}>
                <div className={styles.dateInputGroup}>
                  <label>From</label>
                  <input
                    type="date"
                    value={lastActiveRange.from ? lastActiveRange.from.toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const newFrom = e.target.value ? new Date(e.target.value) : null;
                      setLastActiveRange(prev => ({ ...prev, from: newFrom }));
                      setPageOffset(0);
                    }}
                    className={styles.dateInput}
                  />
                </div>
                <div className={styles.dateInputGroup}>
                  <label>To</label>
                  <input
                    type="date"
                    value={lastActiveRange.to ? lastActiveRange.to.toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const newTo = e.target.value ? new Date(e.target.value) : null;
                      setLastActiveRange(prev => ({ ...prev, to: newTo }));
                      setPageOffset(0);
                    }}
                    className={styles.dateInput}
                  />
                </div>
                {(lastActiveRange.from || lastActiveRange.to) && (
                  <button 
                    onClick={clearLastActiveFilter}
                    className={styles.clearRangeBtn}
                  >
                    Clear Last Active Filter
                  </button>
                )}
              </div>
            )}
            
            {/* Date Range Picker - Member Since */}
            {activeDateFilter === 'memberSince' && (
              <div className={styles.dateRangePanel}>
                <div className={styles.dateInputGroup}>
                  <label>From</label>
                  <input
                    type="date"
                    value={memberSinceRange.from ? memberSinceRange.from.toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const newFrom = e.target.value ? new Date(e.target.value) : null;
                      setMemberSinceRange(prev => ({ ...prev, from: newFrom }));
                      setPageOffset(0);
                    }}
                    className={styles.dateInput}
                  />
                </div>
                <div className={styles.dateInputGroup}>
                  <label>To</label>
                  <input
                    type="date"
                    value={memberSinceRange.to ? memberSinceRange.to.toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const newTo = e.target.value ? new Date(e.target.value) : null;
                      setMemberSinceRange(prev => ({ ...prev, to: newTo }));
                      setPageOffset(0);
                    }}
                    className={styles.dateInput}
                  />
                </div>
                {(memberSinceRange.from || memberSinceRange.to) && (
                  <button 
                    onClick={clearMemberSinceFilter}
                    className={styles.clearRangeBtn}
                  >
                    Clear Member Since Filter
                  </button>
                )}
              </div>
            )}
            
            {/* No filter type selected message */}
            {!activeDateFilter && (
              <div className={styles.noFilterSelected}>
                <p style={{fontSize:10,padding:15,textAlign:'center'}} className={styles.noFilterText}>Select Last Active or Member Since to filter by date range</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const currentPage = Math.floor(pageOffset / pageSize) + 1;

  const handleUserClick = (userId: string) => {
    navigate(`/user/${userId}`);
  };

  const truncateText = (text: string, maxLength: number = 30) => {
    if (!text) return 'none';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className={`${styles.container} ${isDarkMode ? styles.darkMode : ''}`}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Users</h1>
            <p className={styles.subtitle}>User Management</p>
          </div>
          <div className={styles.headerActions}>
            <button className={styles.addUserBtn} onClick={() => setShowAddDialog(true)}>
              <UserPlus size={16} /> Add User
            </button>
            <button className={styles.iconBtn} onClick={handleFullRefresh} disabled={loading || isRefreshing} title="Refresh">
              <RefreshRoundedIcon className={loading || isRefreshing ? styles.spinning : ''} />
            </button>
            <button className={styles.iconBtn} onClick={() => setIsDarkMode(!isDarkMode)} title={isDarkMode ? "Light Mode" : "Dark Mode"}>
              {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </button>
          </div>
        </div>

        {/* Status Filter Cards */}
        <div className={styles.statusCards}>
          {filters.map(filter => (
            <button
              key={filter.id}
              className={`${styles.statusCard} ${
                activeFilter === filter.id ? styles.activeStatusCard : ''
              } ${
                filter.id === 'active' ? styles.activeCard :
                filter.id === 'deactivated' ? styles.deactivatedCard :
                filter.id === 'deleted' ? styles.deletedCard :
                styles.allCard
              }`}
              onClick={() => handleStatusFilter(filter.id)}
            >
              <span className={styles.statusLabel}>{filter.label}</span>
              <span className={styles.statusCount}>({filter.count})</span>
            </button>
          ))}
        </div>

        {/* Filter Actions */}
        <div className={styles.filterSection}>
          <div className={styles.filterActions}>
            <div className={styles.searchWrapper}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by name/email/phone..."
                className={styles.searchInput}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {searchQuery && (
                <button className={styles.clearSearch} onClick={() => handleSearch('')}>
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Client Type Dropdown */}
            <div className={styles.dropdown}>
              <button 
                className={`${styles.dropdownButton} ${clientType ? styles.activeFilter : ''}`}
                onClick={() => setShowClientTypeFilter(!showClientTypeFilter)}
              >
                <span>Client Type {clientType ? `: ${clientType}` : ''}</span>
                <ChevronDown size={14} />
              </button>
              {showClientTypeFilter && (
                <div className={styles.dropdownMenu}>
                  {clientTypes.map(type => (
                    <div
                      key={type}
                      className={`${styles.dropdownItem} ${clientType === type || (clientType === null && type === 'All') ? styles.selected : ''}`}
                      onClick={() => handleClientTypeChange(type)}
                    >
                      {type === 'All' ? 'All Client Types' : type}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Filter by Team Dropdown - single select */}
            <div className={styles.teamFilterContainer}>
              <button 
                className={`${styles.teamFilterBtn} ${selectedTeam ? styles.activeFilter : ''}`}
                onClick={() => setShowTeamFilter(!showTeamFilter)}
              >
                <Filter size={14} />
                {selectedTeam ? selectedTeamLabel : 'Filter by Team'}
                {selectedTeam && (
                  <span
                    onClick={(e) => { e.stopPropagation(); clearTeamFilters(); }}
                    style={{ marginLeft: 4, cursor: 'pointer', opacity: 0.7 }}
                  >
                    <X size={12} />
                  </span>
                )}
                <ChevronDown size={14} />
              </button>
              
              {showTeamFilter && (
                <div className={styles.teamFilterMenu}>
                  <div className={styles.teamFilterHeader}>
                    <span>Select Team</span>
                    {selectedTeam && <button onClick={clearTeamFilters}>Clear</button>}
                  </div>
                  <div className={styles.teamFilterSearchWrapper}>
                    <Search className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Search teams..."
                      className={styles.teamFilterSearchInput}
                      value={teamFilterSearch}
                      onChange={(e) => setTeamFilterSearch(e.target.value)}
                    />
                    {teamFilterSearch && (
                      <button
                        className={styles.clearSearch}
                        onClick={() => setTeamFilterSearch('')}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className={styles.teamFilterList}>
                    {teamsLoading ? (
                      <div className={styles.loadingText}>Loading teams...</div>
                    ) : filteredTeamsList.length > 0 ? (
                      filteredTeamsList.map((team) => (
                        <div
                          key={`${team.value}-${team.leagueId || 'none'}`}
                          className={`${styles.teamCheckbox} ${selectedTeam === team.value ? styles.selected : ''}`}
                          onClick={() => handleTeamFilter(team.value)}
                        >
                          <input
                            type="radio"
                            checked={selectedTeam === team.value}
                            readOnly
                          />
                          <span>{team.label}</span>
                        </div>
                      ))
                    ) : (
                      <div className={styles.noDataText}>No teams found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Combined Date Filter */}
            {renderCombinedDateFilter()}

            <button className={styles.exportBtn} onClick={handleExport} disabled={isExporting || totalCount === 0}>
              <Download size={16} />
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading users...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={styles.errorState}>
            <AlertCircle size={48} />
            <h3>Error Loading Users</h3>
            <p>{error.message}</p>
            <button onClick={handleRefresh} className={styles.retryBtn}>Retry</button>
          </div>
        )}

        {/* Users Table */}
        {!loading && !error && (
          <>
            <div className={styles.tableContainer} ref={tableContainerRef}>
              <table className={styles.usersTable}>
                <thead>
                  <tr>
                    <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('name')}
                  aria-sort={getSortAria('name')}
                  title="Sort by name"
                >
                  <span>NAME</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('name')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('email')}
                  aria-sort={getSortAria('email')}
                  title="Sort by email"
                >
                  <span>EMAIL</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('email')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('status')}
                  aria-sort={getSortAria('status')}
                  title="Sort by status"
                >
                  <span>STATUS</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('status')}</span>
                </th>
                <th
                  className={`${styles.sortableHeader} ${styles.emphasisHeader}`}
                  onClick={() => handleSortColumn('clientType')}
                  aria-sort={getSortAria('clientType')}
                  title="Sort by client type"
                >
                  <span>CLIENT TYPE</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('clientType')}</span>
                </th>
                <th
                  className={`${styles.sortableHeader} ${styles.salesRepHeader}`}
                  onClick={() => handleSortColumn('salesRep')}
                  aria-sort={getSortAria('salesRep')}
                  title="Sort by sales rep"
                >
                  <span>SALES REP</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('salesRep')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('affiliate')}
                  aria-sort={getSortAria('affiliate')}
                  title="Sort by affiliate"
                >
                  <span>AFFILIATE</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('affiliate')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('teams')}
                  aria-sort={getSortAria('teams')}
                  title="Sort by teams"
                >
                  <span>TEAMS</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('teams')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('lastActive')}
                  aria-sort={getSortAria('lastActive')}
                  title="Sort by last active"
                >
                  <span>LAST ACTIVE</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('lastActive')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('memberSince')}
                  aria-sort={getSortAria('memberSince')}
                  title="Sort by member since"
                >
                  <span>MEMBER SINCE</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('memberSince')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('activeTickets')}
                  aria-sort={getSortAria('activeTickets')}
                  title="Sort by active tickets"
                >
                  <span>ACTIVE TICKETS</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('activeTickets')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('totalListed')}
                  aria-sort={getSortAria('totalListed')}
                  title="Sort by total listed"
                >
                  <span>TOTAL LISTED</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('totalListed')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('potentialPayout')}
                  aria-sort={getSortAria('potentialPayout')}
                  title="Sort by potential payout"
                >
                  <span>POTENTIAL PAYOUT</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('potentialPayout')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('totalSold')}
                  aria-sort={getSortAria('totalSold')}
                  title="Sort by total sold"
                >
                  <span>TOTAL SOLD</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('totalSold')}</span>
                </th>
                <th
                  className={styles.sortableHeader}
                  onClick={() => handleSortColumn('totalRevenue')}
                  aria-sort={getSortAria('totalRevenue')}
                  title="Sort by total revenue"
                >
                  <span>TOTAL REVENUE</span>
                  <span className={styles.sortIndicator}>{getSortIndicator('totalRevenue')}</span>
                </th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user) => (
                    <tr key={user.u_id} className={styles.userRow}>
                      <td>
                        <span className={styles.userName} onClick={() => handleUserClick(user.u_id)}>
                          {user.u_full_name || `${user.u_first_name} ${user.u_last_name}`}
                        </span>
                      </td>
                      <td>
                        <span className={styles.userEmail}>{user.u_email_id}</span>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${
                          user.u_delete_requested ? styles.deleted : 
                          user.u_is_active ? styles.active : styles.deactivated
                        }`}>
                          {getStatusIcon(user)}
                          {getStatusText(user)}
                        </span>
                      </td>

                      <td>
                        <div className={styles.clientTypeCell}>
                          <span className={`${styles.clientTypeBadge} ${
                            getClientType(user) === 'concierge'
                              ? styles.concierge
                              : getClientType(user) === 'self-pricing'
                              ? styles.selfPricing
                              : styles.none
                          }`}>
                            {getClientType(user)}
                          </span>
                          {canManageSuperUserTable &&
                            normalizeClientType(getClientType(user)) === "self-pricing" && (
                              <div className={styles.superUserToggleRow}>
                                <span className={styles.superUserToggleLabel}>Super user</span>
                                {superUserToggleUserId === user.u_id ? (
                                  <Loader2 size={14} className={styles.spinning} aria-hidden />
                                ) : (
                                  <label className={styles.superUserSwitch}>
                                    <input
                                      type="checkbox"
                                      role="switch"
                                      checked={isSuperUserToggleChecked(user.u_id)}
                                      disabled={!user.u_is_active || !!user.u_delete_requested}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleSuperUserTableToggle(user, e.target.checked);
                                      }}
                                      aria-label={
                                        isSuperUserToggleChecked(user.u_id)
                                          ? "Remove from super user table"
                                          : "Add to super user table"
                                      }
                                    />
                                    <span className={styles.superUserSlider} aria-hidden />
                                  </label>
                                )}
                              </div>
                            )}
                        </div>
                      </td>

                      {/* SALES REP Column with Dropdown - active users only */}
                      <td>
                        {(user.u_is_active && !user.u_delete_requested) ? (
                          <>
                            <div className={styles.salesRepCell}>
                              {isMarketingUser ? (
                                <span className={styles.salesRepText} title={getSalesRep(user)}>
                                  {truncateText(getSalesRep(user), 20)}
                                </span>
                              ) : (
                                <>
                                  <button
                                    className={styles.salesRepDropdownBtn}
                                    ref={(el) => {
                                      const k = `sales-${user.u_id}`;
                                      if (el) dropdownAnchorRefs.current.set(k, el);
                                      else dropdownAnchorRefs.current.delete(k);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const next = showSalesRepDropdown === user.u_id ? null : user.u_id;
                                      if (next) {
                                        const el = dropdownAnchorRefs.current.get(`sales-${user.u_id}`);
                                        if (el) {
                                          const r = el.getBoundingClientRect();
                                          setSalesRepMenuPos({ top: Math.round(r.bottom), left: Math.round(r.left), width: Math.round(r.width) });
                                        } else {
                                          setSalesRepMenuPos(null);
                                        }
                                      } else {
                                        setSalesRepMenuPos(null);
                                      }
                                      setShowSalesRepDropdown(next);
                                    }}
                                  >
                                    <span className={styles.salesRepText} title={getSalesRep(user)}>
                                      {truncateText(getSalesRep(user), 20)}
                                    </span>
                                    <ChevronDown size={12} className={styles.dropdownIcon} />
                                  </button>
                                  {showSalesRepDropdown === user.u_id && salesRepMenuPos && createPortal(
                                    <div
                                      className={styles.salesRepDropdownMenu}
                                      style={{ position: 'fixed', top: salesRepMenuPos.top, left: salesRepMenuPos.left, minWidth: Math.max(180, salesRepMenuPos.width),height:'45%' }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className={styles.dropdownSearchWrapper} onClick={(e) => e.stopPropagation()}>
                                        <Search size={14} className={styles.dropdownSearchIcon} />
                                        <input
                                          type="text"
                                          placeholder="Search sales reps..."
                                          value={salesRepSearchTerm}
                                          onChange={(e) => setSalesRepSearchTerm(e.target.value)}
                                          className={styles.dropdownSearchInput}
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        {salesRepSearchTerm && (
                                          <button className={styles.clearSearch} onClick={(e) => { e.stopPropagation(); setSalesRepSearchTerm(''); }}>
                                            <X size={12} />
                                          </button>
                                        )}
                                      </div>

                                      <div
                                        className={`${styles.salesRepDropdownItem} ${getSalesRep(user) === 'none' ? styles.selected : ''}`}
                                        onClick={() => { setShowSalesRepDropdown(null); setSalesRepMenuPos(null); updateUserSalesRep(user.u_id, ''); }}
                                      >
                                        None
                                      </div>

                                      {salesRepsLoading ? (
                                        <div className={styles.loadingText}>Loading...</div>
                                      ) : (filteredSalesReps.length > 0) ? (
                                        filteredSalesReps.map(rep => {
                                          const repName = `${rep.sr_first_name || ''} ${rep.sr_last_name || ''}`.trim() || rep.sr_email;
                                          return (
                                            <div
                                              key={rep.sr_id}
                                              className={`${styles.salesRepDropdownItem} ${user.u_sales_reps_id === rep.sr_id ? styles.selected : ''}`}
                                              onClick={() => {
                                                if (user.u_sales_reps_id === rep.sr_id) {
                                                  showError('Already assigned');
                                                  return;
                                                }
                                                setShowSalesRepDropdown(null);
                                                setSalesRepMenuPos(null);
                                                updateUserSalesRep(user.u_id, rep.sr_id);
                                              }}
                                              title={repName}
                                            >
                                              {truncateText(repName, 25)}
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <div className={styles.noDataText}>No sales reps</div>
                                      )}
                                    </div>,
                                    document.body
                                  )}
                                </>
                              )}
                            </div>
                          </>
                        ) : (
                          <span className={styles.noDataText}>—</span>
                        )}
                      </td>

                      {/* AFFILIATE Column with Dropdown - active users only */}
                      <td>
                        <div className={styles.dropdownCell}>
                          {(user.u_is_active && !user.u_delete_requested) ? (
                            <>
                              <button
                                className={styles.cellDropdownBtn}
                                ref={(el) => {
                                  const k = `aff-${user.u_id}`;
                                  if (el) dropdownAnchorRefs.current.set(k, el);
                                  else dropdownAnchorRefs.current.delete(k);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const next = showAffiliateDropdown === user.u_id ? null : user.u_id;
                                  if (next) {
                                    const el = dropdownAnchorRefs.current.get(`aff-${user.u_id}`);
                                    if (el) {
                                      const r = el.getBoundingClientRect();
                                      setAffiliateMenuPos({ top: Math.round(r.bottom), left: Math.round(r.left), width: Math.round(r.width) });
                                    } else setAffiliateMenuPos(null);
                                  } else {
                                    setAffiliateMenuPos(null);
                                  }
                                  setShowAffiliateDropdown(next);
                                }}
                              >
                                <span className={styles.affiliateText} title={getAffiliate(user)}>
                                  {truncateText(getAffiliate(user), 25)}
                                </span>
                                <ChevronDown size={12} />
                              </button>
                              {showAffiliateDropdown === user.u_id && affiliateMenuPos && createPortal(
                                <div className={styles.cellDropdownMenu} onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', top: affiliateMenuPos.top, left: affiliateMenuPos.left, minWidth: Math.max(180, affiliateMenuPos.width) }}>
                                  <div className={styles.dropdownSearchWrapper} onClick={(e) => e.stopPropagation()}>
                                    <Search size={14} className={styles.dropdownSearchIcon} />
                                    <input
                                      type="text"
                                      placeholder="Search affiliates..."
                                      value={affiliateSearchTerm}
                                      onChange={(e) => setAffiliateSearchTerm(e.target.value)}
                                      className={styles.dropdownSearchInput}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    {affiliateSearchTerm && (
                                      <button className={styles.clearSearch} onClick={(e) => { e.stopPropagation(); setAffiliateSearchTerm(''); }}>
                                        <X size={12} />
                                      </button>
                                    )}
                                  </div>

                                  <div
                                    className={`${styles.cellDropdownItem} ${getAffiliate(user) === 'None' ? styles.selected : ''}`}
                                    onClick={() => { setShowAffiliateDropdown(null); setAffiliateMenuPos(null); updateUserAffiliate(user.u_id, ''); }}
                                  >
                                    None
                                  </div>

                                  {affiliatesLoading ? (
                                    <div className={styles.loadingText}>Loading affiliates...</div>
                                  ) : (filteredAffiliates.length > 0) ? (
                                    filteredAffiliates.map((affiliate: { a_id: string; u_id: string | null; u_full_name: string; u_email_id: string }) => {
                                      const isSelected = user.u_affiliate_id === affiliate.a_id || getAffiliate(user) === affiliate.u_full_name
                                      return (
                                        <div
                                          key={affiliate.a_id}
                                          className={`${styles.cellDropdownItem} ${isSelected ? styles.selected : ''}`}
                                          onClick={() => { setShowAffiliateDropdown(null); setAffiliateMenuPos(null); updateUserAffiliate(user.u_id, affiliate.a_id); }}
                                          title={affiliate.u_full_name}
                                        >
                                          <input 
                                            type="radio" 
                                            checked={isSelected} 
                                            readOnly 
                                            className={styles.radio}
                                          />
                                          <span>{truncateText(affiliate.u_full_name, 25)}</span>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <div className={styles.noDataText}>No affiliates available</div>
                                  )}
                                </div>,
                                document.body
                              )}
                            </>
                          ) : (
                            <span className={styles.noDataText}>—</span>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className={styles.dropdownCell}>
                          {(user.u_is_active && !user.u_delete_requested) ? (
                            <>
                              <button
                                className={styles.cellDropdownBtn}
                                ref={(el) => {
                                  const k = `teams-${user.u_id}`;
                                  if (el) dropdownAnchorRefs.current.set(k, el);
                                  else dropdownAnchorRefs.current.delete(k);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const next = showTeamsDropdown === user.u_id ? null : user.u_id;
                                  if (next) {
                                    const el = dropdownAnchorRefs.current.get(`teams-${user.u_id}`);
                                    if (el) {
                                      const r = el.getBoundingClientRect();
                                      setTeamsMenuPos({ top: Math.round(r.bottom), left: Math.round(r.left), width: Math.round(r.width) });
                                    } else {
                                      setTeamsMenuPos(null);
                                    }
                                  } else {
                                    setTeamsMenuPos(null);
                                  }
                                  setShowTeamsDropdown(next);
                                }}
                              >
                                <span className={styles.affiliateText} title={getUserTeams(user)}>
                                  {truncateText(getUserTeams(user), 20)}
                                </span>
                                <ChevronDown size={12} />
                              </button>
                              {showTeamsDropdown === user.u_id && teamsMenuPos && createPortal(
                                <div className={`${styles.cellDropdownMenu} ${styles.teamsDropdownMenu}`} style={{ position: 'fixed', top: teamsMenuPos.top, left: teamsMenuPos.left, minWidth: Math.max(240, teamsMenuPos.width) }} onClick={(e) => e.stopPropagation()}>
                                  <div className={styles.dropdownHeader}>
                                    <span>Teams</span>
                                    <button onClick={() => clearUserTeams(user.u_id)}>Clear All</button>
                                  </div>

                                  <div className={styles.teamFilterControls}>
                                    <div className={styles.teamSearchWrapper} onClick={(e) => e.stopPropagation()}>
                                      <Search size={14} className={styles.teamSearchIcon} />
                                      <input
                                        type="text"
                                        placeholder="Search teams..."
                                        value={teamSearchTerm}
                                        onChange={(e) => setTeamSearchTerm(e.target.value)}
                                        className={styles.teamSearchInput}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      {teamSearchTerm && (
                                        <button
                                          className={styles.clearTeamSearch}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTeamSearchTerm('');
                                          }}
                                        >
                                          <X size={12} />
                                        </button>
                                      )}
                                    </div>

                                    <select
                                      value={teamLeagueFilter}
                                      onChange={(e) => setTeamLeagueFilter(e.target.value)}
                                      className={styles.teamLeagueSelect}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <option value="all">All Leagues</option>
                                      {allLeaguesData?.leagues?.map((league: League) => (
                                        <option key={league.l_id} value={league.l_id}>
                                          {league.l_name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className={styles.dropdownList}>
                                    {(() => {
                                      const currentTeamIds: string[] = Array.isArray(teamsWithSoldData?.user_preference)
                                        ? teamsWithSoldData.user_preference
                                            .map((pref: any) => pref?.lc_id)
                                            .filter((id: string): id is string => Boolean(id))
                                        : [];
                                      let filteredTeams = teamsList.filter((team) => {
                                        const search = teamSearchTerm.trim().toLowerCase();
                                        const matchesSearch = search === '' || team.label.toLowerCase().includes(search);
                                        const matchesLeague = teamLeagueFilter === 'all' || team.leagueId === teamLeagueFilter;
                                        return matchesSearch && matchesLeague;
                                      });

                                      filteredTeams.sort((a, b) => {
                                        const aAssigned = currentTeamIds.includes(a.value);
                                        const bAssigned = currentTeamIds.includes(b.value);
                                        if (aAssigned && !bAssigned) return -1;
                                        if (!aAssigned && bAssigned) return 1;
                                        return a.label.localeCompare(b.label);
                                      });

                                      if (filteredTeams.length === 0) {
                                        return (
                                          <div className={styles.noDataText} style={{ fontSize: '12px', padding: '8px' }}>
                                            No teams match
                                          </div>
                                        );
                                      }

                                      return filteredTeams.map((team) => {
                                        const isChecked = currentTeamIds.includes(team.value);
                                        const soldDisabled = Boolean(team.categoryId && teamsWithSoldStatus.get(team.categoryId));
                                        return (
                                          <div
                                            key={team.value}
                                            className={styles.dropdownCheckboxItem}
                                            style={{ justifyContent: 'space-between', alignItems: 'center' }}
                                          >
                                            <div
                                              className={`${styles.dropdownCheckboxItemRow} ${soldDisabled && isChecked ? styles.dropdownCheckboxItemRowDisabled : ''}`}
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                if (soldDisabled && isChecked) return;
                                                await toggleUserTeam(user.u_id, currentTeamIds, team.value);
                                              }}
                                            >
                                              <input type="checkbox" checked={isChecked} readOnly disabled={soldDisabled && isChecked} />
                                              <span className={styles.dropdownItemText} style={soldDisabled ? { opacity: 0.6 } : undefined}>
                                                {team.label}
                                              </span>
                                              {soldDisabled && isChecked && (
                                                <span className={styles.dropdownMutedBadge}>
                                                  Sold
                                                </span>
                                              )}
                                            </div>
                                            {isChecked && (
                                              <button
                                                type="button"
                                                className={styles.removeTeamBtn}
                                                onClick={async (e) => {
                                                  e.stopPropagation();
                                                  if (soldDisabled) return;
                                                  await toggleUserTeam(user.u_id, currentTeamIds, team.value);
                                                }}
                                                disabled={soldDisabled}
                                                title={soldDisabled ? 'Cannot remove team with sold tickets' : 'Remove team'}
                                              >
                                                Remove
                                              </button>
                                            )}
                                          </div>
                                        );
                                      });
                                    })()}
                                  </div>
                                </div>,
                                document.body
                              )}
                            </>
                          ) : (
                            <span className={styles.noDataText}>—</span>
                          )}
                        </div>
                      </td>

                      <td>
                        <span className={styles.lastActive}>{formatDateTime(user.u_last_online)}</span>
                      </td>
                      <td>
                        <span className={styles.memberSince}>{formatDate(user.u_created_at)}</span>
                      </td>
                      <td>
                        <span className={styles.activeTickets}>{getActiveTickets(user)}</span>
                      </td>
                      <td>
                        <span className={styles.totalListed}>{getTotalListed(user)}</span>
                      </td>
                      <td>
                        <span className={styles.potentialPayout}>{getPotentialPayout(user)}</span>
                      </td>
                      <td>
                        <span className={styles.totalSold}>{getTotalSold(user)}</span>
                      </td>
                      <td>
                        <span className={styles.totalRevenue}>{formatCurrency(getTotalRevenue(user))}</span>
                      </td>
                      <td>
                        <div className={styles.actionMenuContainer}>
                          <button 
                            className={styles.actionBtn}
                            type="button"
                            onClick={(e) => toggleActionMenu(user.u_id, e)}
                          >
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {paginatedUsers.length === 0 && (
                <div className={styles.noResults}>
                  <Search size={48} />
                  <h3>No users found</h3>
                  <p>Try adjusting your search or filters</p>
                </div>
              )}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button onClick={() => handlePageChange(pageOffset - pageSize)} disabled={pageOffset === 0} className={styles.paginationBtn}>
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
                <button onClick={() => handlePageChange(pageOffset + pageSize)} disabled={pageOffset + pageSize >= totalCount} className={styles.paginationBtn}>
                  <ChevronRight size={16} />
                </button>
                <select value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))} className={styles.pageSizeSelect}>
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
            )}
          </>
        )}
      </div>

      {openActionMenu && actionMenuPos && actionMenuUser && createPortal(
        <div
          className={styles.actionMenu}
          style={{
            position: 'fixed',
            top: actionMenuPos.top,
            left: actionMenuPos.left,
            minWidth: actionMenuPos.width,
            zIndex: 2000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button className={styles.actionMenuItem} onClick={(e) => handleViewDetails(actionMenuUser, e)}>
            <Eye size={14} /> View Details
          </button>
          <button className={styles.actionMenuItem} onClick={(e) => handleViewAnalytics(actionMenuUser, e)}>
            <TrendingUp size={14} /> View Analytics
          </button>
          <div className={styles.actionMenuDivider}></div>
          <button
            className={`${styles.actionMenuItem} ${
              actionMenuUser.u_is_active ? styles.actionMenuItemWarning : styles.actionMenuItemSuccess
            }`}
            onClick={(e) => handleUpdateUserStatus(actionMenuUser, e)}
          >
            <Clock size={14} />
            {actionMenuUser.u_is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            className={`${styles.actionMenuItem} ${styles.actionMenuItemDanger}`}
            onClick={(e) => handleDeleteUser(actionMenuUser, e)}
            disabled={actionMenuUser.u_delete_requested}
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>,
        document.body,
      )}

      {/* Add User Dialog */}
      {/* DELETE CONFIRMATION OVERLAY */}
      {pendingDeleteUser && (
        <div className={styles.deleteConfirmOverlay}>
          <div className={styles.deleteConfirmContainer}>
            <h3>Delete User</h3>
            <p>
              Are you sure you want to delete{" "}
              <strong>
                {pendingDeleteUser.u_full_name ||
                  `${pendingDeleteUser.u_first_name || ''} ${pendingDeleteUser.u_last_name || ''}`.trim() ||
                  'this user'}
              </strong>
            </p>
            <div className={styles.deleteConfirmActions}>
              <button className={styles.cancelButton} onClick={() => setPendingDeleteUser(null)}>
                Cancel
              </button>
              <button className={styles.deleteConfirmBtn} onClick={confirmDeleteUser}>
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddDialog && (
        <div className={styles.dialogOverlay} onClick={() => setShowAddDialog(false)}>
          <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.dialogHeader}>
              <h2 className={styles.dialogTitle}>Add New User</h2>
              <button className={styles.dialogCloseBtn} onClick={() => { setShowAddDialog(false); resetNewUserForm(); }}>
                <X size={18} />
              </button>
            </div>
            
            <div className={styles.dialogBody}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>First Name</label>
<input
  type="text"
  placeholder="John"
  value={newUser.first_name}
  onChange={(e) => {
    const value = e.target.value;

    setNewUser({ ...newUser, first_name: value });

    if (!nameRegex.test(value)) {
      setFormErrors({
        ...formErrors,
        first_name: "First name is not valid",
      });
    } else {
      setFormErrors({
        ...formErrors,
        first_name: undefined,
      });
    }
  }}
  className={`${styles.formInput} ${
    formErrors.first_name ? styles.inputError : ""
  }`}
/>
                  {formErrors.first_name && <span className={styles.errorText}>{formErrors.first_name}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Last Name</label>
<input
  type="text"
  placeholder="Doe"
  value={newUser.last_name}
  onChange={(e) => {
    const value = e.target.value;

    setNewUser({ ...newUser, last_name: value });

    if (!nameRegex.test(value)) {
      setFormErrors({
        ...formErrors,
        last_name: "Last name is not valid",
      });
    } else {
      setFormErrors({
        ...formErrors,
        last_name: undefined,
      });
    }
  }}
  className={`${styles.formInput} ${
    formErrors.last_name ? styles.inputError : ""
  }`}
/>
                  {formErrors.last_name && <span className={styles.errorText}>{formErrors.last_name}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Business Name</label>
                  <input type="text" placeholder="Business name (optional)" value={newUser.business_name}
                    onChange={(e) => setNewUser({ ...newUser, business_name: e.target.value })}
                    className={styles.formInput} />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Email</label>
                  <input type="email" placeholder="john.doe@example.com" value={newUser.email}
                    onChange={(e) => { setNewUser({ ...newUser, email: e.target.value }); if (formErrors.email) setFormErrors({ ...formErrors, email: undefined }); }}
                    className={`${styles.formInput} ${formErrors.email ? styles.inputError : ''}`} />
                  {formErrors.email && <span className={styles.errorText}>{formErrors.email}</span>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Phone</label>
                  <div className={styles.phoneInputGroup}>
                    <input type="text" placeholder="+1" value={newUser.country_code}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewUser({ ...newUser, country_code: value });
                        setFormErrors({
                          ...formErrors,
                          country_code: value.trim()
                            ? validateCountryCode(value)
                              ? undefined
                              : 'Invalid country code'
                            : 'Country code is required'
                        });
                      }}
                      className={`${styles.phoneCodeInput} ${formErrors.country_code ? styles.inputError : ''}`} />
                    <input type="tel" placeholder="5551234567" value={newUser.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setNewUser({ ...newUser, phone: value });
                        setFormErrors({
                          ...formErrors,
                          phone: value.trim()
                            ? validatePhoneNumber(value)
                              ? undefined
                              : 'Phone number must be exactly 10 digits'
                            : 'Phone number is required'
                        });
                      }}
                      className={`${styles.phoneNumberInput} ${formErrors.phone ? styles.inputError : ''}`} />
                  </div>
                  {formErrors.country_code && <span className={styles.errorText}>{formErrors.country_code}</span>}
                  {formErrors.phone && <span className={styles.errorText}>{formErrors.phone}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Client Type</label>
                  <select
                    value={newUser.client_type}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setNewUser({
                        ...newUser,
                        client_type: nextType,
                        super_user_percentage: nextType === "self-pricing" ? "15" : defaultConciergePercentage,
                      });
                      if (formErrors.super_user_percentage) {
                        setFormErrors({ ...formErrors, super_user_percentage: undefined });
                      }
                    }}
                    className={styles.formSelect}
                  >
                    <option value="concierge">Concierge</option>
                    <option value="self-pricing">Self Pricing</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Percentage (%)</label>
                  <input
                    type="number"
                    min="0.01"
                    max="100"
                    step="0.01"
                    placeholder="Enter percentage"
                    value={newUser.super_user_percentage}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewUser({ ...newUser, super_user_percentage: value });
                      setFormErrors({
                        ...formErrors,
                        super_user_percentage: value.trim()
                          ? !Number.isFinite(Number(value)) || Number(value) <= 0 || Number(value) > 100
                            ? 'Percentage must be greater than 0 and at most 100'
                            : undefined
                          : 'Percentage is required'
                      });
                    }}
                    className={`${styles.formInput} ${formErrors.super_user_percentage ? styles.inputError : ''}`}
                  />
                  {formErrors.super_user_percentage && (
                    <span className={styles.errorText}>{formErrors.super_user_percentage}</span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Assign Sales Rep</label>
                  <select
                    value={newUser.sales_rep_id}
                    onChange={(e) => setNewUser({ ...newUser, sales_rep_id: e.target.value })}
                    className={styles.formSelect}
                    disabled={isMarketingUser || !canReadSalesReps}
                  >
                    <option value="">None</option>
                    {salesRepsLoading ? (
                      <option disabled>Loading...</option>
                    ) : (
                      salesRepsList.map(rep => {
                        const repName = `${rep.sr_first_name || ''} ${rep.sr_last_name || ''}`.trim() || rep.sr_email;
                        return (
                          <option key={rep.sr_id} value={rep.sr_id}>{repName}</option>
                        );
                      })
                    )}
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Password</label>
                  <div className={styles.passwordInputWrapper}>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Min 10 chars with one special character"
                      value={newUser.password}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewUser({ ...newUser, password: value });
                        setFormErrors({
                          ...formErrors,
                          password: value
                            ? validatePasswordValue(value)
                              ? undefined
                              : 'Password must be at least 10 characters and contain one special character'
                            : 'Password is required',
                          confirmPassword: newUser.confirmPassword && value !== newUser.confirmPassword
                            ? 'Passwords do not match'
                            : undefined
                        });
                      }}
                      className={`${styles.formInput} ${formErrors.password ? styles.inputError : ''}`}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className={styles.passwordToggleBtn}
                      onClick={() => setShowPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {formErrors.password && <span className={styles.errorText}>{formErrors.password}</span>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Confirm Password</label>
                  <div className={styles.passwordInputWrapper}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter password"
                      value={newUser.confirmPassword}
                      onChange={(e) => {
                        const value = e.target.value;
                        setNewUser({ ...newUser, confirmPassword: value });
                        setFormErrors({
                          ...formErrors,
                          confirmPassword: value
                            ? value === newUser.password
                              ? undefined
                              : 'Passwords do not match'
                            : 'Confirm password is required'
                        });
                      }}
                      className={`${styles.formInput} ${formErrors.confirmPassword ? styles.inputError : ''}`}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className={styles.passwordToggleBtn}
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {formErrors.confirmPassword && <span className={styles.errorText}>{formErrors.confirmPassword}</span>}
                </div>
              </div>
            </div>

            <div className={styles.dialogFooter}>
              <button className={styles.cancelButton} onClick={() => { setShowAddDialog(false); resetNewUserForm(); }}>Cancel</button>
              <button className={styles.submitButton} onClick={handleAddUser} disabled={addUserLoading}>
                {addUserLoading ? (<><Loader2 size={16} className={styles.spinning} /> Adding...</>) : 'Add User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
